import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExpand, faCompress } from "@fortawesome/free-solid-svg-icons";

import { FaChevronDown } from "react-icons/fa6";

import {
  FiDownload,
  FiTrash2,
} from "react-icons/fi";
import { PiShareFat } from "react-icons/pi";

import careplan_modal from "../../styles/patient-page-modules/carePlanModal.module.css";
import conditionSelector from "../patient-detail/patient-modals/add-condition/condition-selector.module.css";
import {
  getChronicConditionOptions,
  getDrNoteConditions,
} from "../../services/admin/chronicConditions";
import {
  getCarePlan,
  addCarePlan,
  deleteCarePlan,
  getAiCarePlan,
  updateHistoricalCarePlan,
  getYearlyDocumentStatus,
} from "../../services/patient/carePlan";
import {
  getPatientGeneralDetails,
  getPatientNotes,
} from "../../services/patient/details";
import { shareAuditReport } from "../../services/patient/documents";
import toast from "react-hot-toast";
import Spinner from "../spinner";
import Button from "../button-v1";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogBody,
} from "../ui/dialog";
import { formatDate } from "../../utils/helper";
import { Dropdown, DropdownItem } from "../ui/dropdown";
import SharingModal from "../shared-modules/share-modal";
import Tooltip from "../shared-modules/Tooltip";

import generateFinalizeCareplanPDF from "./finalizecareplan-pdf/FinalizeCareplanPDF";
import logo from "../../assets/images/toca_health_pdf_logo.png";
import AlertDialog from "../../components/alert-dialogue";

import FreeTextSection from "./patient-modals/freeTextSection";
import { wasCreatedOnOrAfterSymptomsCutoff } from "../../utils/symptomTriState";
import SymptomTriStateList from "./patient-modals/symptomTriStateList";
import PrognosisTemplateReference from "./patient-modals/prognosisTemplateReference";
import { ChevronDown, Copy, Indent, Lock } from "lucide-react";
import useRoles from "../../hooks/useRoles";
import CareplanReview from "./careplan-review";
import clsx from "clsx";
import { useToggle } from "../../hooks/useToggle";
import ResetCareplan from "../shared-modules/resetCareplanModal/ResetCareplan";
import CopyForwardModal from "./copy-forward/CopyForwardModal";
import HealthSummaryHistoryModal from "./health-summary-history/HealthSummaryHistoryModal";
import calendarHistoryIcon from "../../assets/icons/calendar-history.svg";

const statusMapping = {
  in_progress: "In Progress",
  completed: "Completed",
  finalized: "Finalized",
};

// Shared single-line field box styling per the approved Figma (SCRUM-469+):
// #F8FAFD background, #C9CBD5 border, 6px radius, 14px text. View-only
// screens drop the box entirely per Figma — just an underline below the
// text, value in navy — so this is a function of isReadOnly rather than a
// static object.
const getFieldBoxStyle = (isReadOnly) =>
  isReadOnly
    ? {
      width: "100%",
      height: "36px",
      padding: "0 12px 0 0",
      border: "none",
      borderBottom: "1px solid #C9CBD5",
      borderRadius: 0,
      backgroundColor: "transparent",
      fontSize: "14px",
      color: "#0D1139",
      boxSizing: "border-box",
      margin: "0px",
    }
    : {
      width: "100%",
      height: "36px",
      padding: "0 12px",
      border: "1px solid #C9CBD5",
      borderRadius: "6px",
      backgroundColor: "#F8FAFD",
      fontSize: "14px",
      color: "#0D1139",
      boxSizing: "border-box",
      margin: "0px",
    };

// Shared auto-growing multi-line field styling (no character limit/counter) —
// used for Sleep/Diet-Water/Exercise and Careplan Notes, which Figma shows
// without numbering, unlike the FreeTextSection fields. Same view-only
// underline treatment as getFieldBoxStyle above.
//
// maxHeight/overflow here override the global ".careplan_wrapper textarea"
// rule's max-height: 250px (carePlanModal.module.css) — without them, the
// JS auto-grow below gets clamped by that CSS max-height once content
// exceeds it, and the browser's default textarea overflow kicks in as an
// internal scrollbar instead of the box actually growing.
const getAutoGrowFieldStyle = (isReadOnly) =>
  isReadOnly
    ? {
      width: "100%",
      minHeight: "36px",
      height: "auto",
      maxHeight: "none",
      overflow: "hidden",
      padding: "9px 12px 9px 0",
      border: "none",
      borderBottom: "1px solid #C9CBD5",
      borderRadius: 0,
      backgroundColor: "transparent",
      fontSize: "14px",
      color: "#0D1139",
      resize: "none",
      outline: "none",
      boxSizing: "border-box",
      fontFamily: "inherit",
    }
    : {
      width: "100%",
      minHeight: "60px",
      height: "auto",
      maxHeight: "none",
      overflow: "hidden",
      padding: "9px 12px",
      border: "1px solid #C9CBD5",
      borderRadius: "6px",
      backgroundColor: "#F8FAFD",
      fontSize: "14px",
      color: "#565975",
      resize: "none",
      outline: "none",
      boxSizing: "border-box",
      fontFamily: "inherit",
    };

// FreeTextSection plus an inline over-limit error, laid out on the same row
// as FreeTextSection's own "x / max" counter (left side vs. its right-aligned
// counter). Kept local so the shared FreeTextSection itself stays unchanged -
// see .limited_field in carePlanModal.module.css for how the error shares the
// counter's line and wraps downward in narrow columns instead of overlapping
// the editor. Declared at module level (not inside CarePlanNew) so it isn't
// re-created every render, which would remount the editor and lose the caret.
const LimitedFreeTextSection = ({ maxLength, value, ...props }) => {
  const length = (value || "").length;
  const overLimit = !props.readOnly && maxLength && length > maxLength;
  return (
    <div className={careplan_modal.limited_field} style={{ width: "100%" }}>
      <FreeTextSection maxLength={maxLength} value={value} {...props} />
      {overLimit ? (
        <span className={careplan_modal.limited_field_error}>
          Max {maxLength.toLocaleString()} characters allowed ({length.toLocaleString()} entered).
        </span>
      ) : null}
    </div>
  );
};

// Section title ("Medication Reconciliation", "Health Summary", etc.) —
// 16px semibold navy, per Figma.
const sectionHeadingStyle = { margin: 0, color: "#0D1139", fontSize: "16px", fontWeight: 600, padding: "10px 0px 5px" };

// Sub-section label ("Physiological Data Averages:") — 14px semibold grey.
const subsectionHeadingStyle = { margin: 0, color: "#565975", fontSize: "14px", fontWeight: 600 };

// Health Summary physiological fields: free text is allowed, and the field
// grows vertically like the others. The unit is shown inside the field.
// One check: blood pressure written as plain numbers ("120/80") needs
// systolic (first number) greater than diastolic (second). Only enforced
// once the field is edited, so older plans' values load and save untouched.
const PHYSIOLOGICAL_FIELD_RULES = {
  weight: { unit: "lbs", placeholder: "e.g. 150.5" },
  blood_glucose: { unit: "mg/dL", placeholder: "e.g. 132" },
  bmi: { unit: "", placeholder: "e.g. 26.3" },
  blood_pressure: { unit: "mmHg", placeholder: "e.g. 120/80" },
  height: { unit: "ft", placeholder: "e.g. 5.75" },
};
const BLOOD_PRESSURE_READING = /^\s*(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)\s*$/;

// Letters, numbers, spaces and only the symbols a vital reading needs:
// / . , - ( ) : ' " & % (e.g. "120/80 (clinic), home 128-146", "5'9\""). Anything
// else ($ # @ % ...) is dropped as the user types.
const PHYSIOLOGICAL_DISALLOWED_CHARS = /[^A-Za-z0-9\s/.,\-():'"&%]/g;
const sanitizePhysiologicalValue = (value) => value.replace(PHYSIOLOGICAL_DISALLOWED_CHARS, "");

// The error message for a value, or null when it's fine.
const getPhysiologicalError = (field, value) => {
  if (field !== "blood_pressure") return null;
  const reading = BLOOD_PRESSURE_READING.exec(value || "");
  if (reading && Number(reading[1]) <= Number(reading[2])) {
    return "Systolic (first number) must be greater than diastolic";
  }
  return null;
};

const isValidPhysiologicalValue = (field, value) => !getPhysiologicalError(field, value);

// Field label (Weight, Sleep, Notes, etc.) — 14px navy, per Figma; grey on
// view-only screens, matching their muted labels (e.g. "Chronic condition").
const getFieldLabelStyle = (isReadOnly) => ({
  color: isReadOnly ? "#565975" : "#0D1139",
  fontSize: "14px",
});

// Shared 35px circular icon-button styling per Figma (expand/share/download).
const circleIconButtonStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "35px",
  height: "35px",
  minWidth: "35px",
  borderRadius: "50%",
  border: "0.8px solid rgba(51,51,51,0.2)",
  backgroundColor: "#F8FAFD",
  boxShadow: "none",
  cursor: "pointer",
  padding: 0,
};

// Left/right scroll arrow for the condition tabs row when it overflows.
const tabsScrollButtonStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "22px",
  height: "22px",
  minWidth: "22px",
  flexShrink: 0,
  border: "1px solid #C9CBD5",
  borderRadius: "4px",
  backgroundColor: "#fff",
  color: "#0D1139",
  cursor: "pointer",
  padding: 0,
};

// Shared "solid navy" button styling per Figma — overrides Button's default
// grey border/shadow/radius, which otherwise shows through inline overrides.
const navyButtonStyle = {
  backgroundColor: "#0D1139",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  boxShadow: "none",
  // padding: "10px 16px",
  fontSize: "13px",
  whiteSpace: "nowrap",
};

const CarePlanNew = ({ isSectionOpen, onSectionClose, isCcmChatStarted, onStartTocaCopilot }) => {
  const {
    isCurrentMonthCareplanExists,
    setIsCurrentMonthCareplanExists,
    careplanMode,
  } = useToggle();
  // Fullscreen toggle for the CCM wizard header (Section 1 scope in SCRUM-468).
  // Separate from RPM's own isCareplanExpand toggle in useToggle() — RPM's
  // expand behavior is untouched, this is a new, CCM-only concern now that
  // the wizard has no fixed height to expand out of.
  const [isCcmFullscreen, setIsCcmFullscreen] = useState(false);
  const { roles, position } = useRoles();
  const dashType = sessionStorage.getItem("_dash");
  const isNurseDashboard = dashType === "nurse";

  const queryClient = useQueryClient();
  const location_id = JSON.parse(sessionStorage.getItem("current_loc"))?.id;
  const patient_id = sessionStorage.getItem("_pt_id");
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  const isPatient =
    JSON.parse(localStorage.getItem("user"))?.account_roles?.[0]?.name ===
    "Patient";
  const user = JSON.parse(localStorage.getItem("user"));
  const role = user?.account_role?.name;

  const [selectedMonth, setSelectedMonth] = useState({
    month: currentMonth,
    year: currentYear,
  });

  // Helper to check if selected month is current or historical (not future)
  const isHistoricalOrCurrentMonth = (selectedMonth, currentMonth, currentYear) => {
    if (!selectedMonth || !selectedMonth.month || !selectedMonth.year) {
      return false;
    }

    // Create Date objects for comparison (handles year boundaries correctly)
    const selectedDate = new Date(selectedMonth.year, selectedMonth.month - 1, 1);
    const currentDate = new Date(currentYear, currentMonth - 1, 1);

    // Allow if selected date is current or in the past
    return selectedDate <= currentDate;
  };

  // Which chronic-condition tab is showing (SCRUM-469). Conditions render as
  // tabs now — only the active one's detail form is visible at a time.
  const [activeConditionKey, setActiveConditionKey] = useState(null);

  // Left/right scroll affordance for the condition tabs row, shown only
  // when the tabs actually overflow the row's width.
  const tabsScrollRef = useRef(null);
  const [canScrollTabsLeft, setCanScrollTabsLeft] = useState(false);
  const [canScrollTabsRight, setCanScrollTabsRight] = useState(false);

  const updateTabsScrollState = () => {
    const el = tabsScrollRef.current;
    if (!el) return;
    setCanScrollTabsLeft(el.scrollLeft > 2);
    setCanScrollTabsRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  };

  const scrollTabsBy = (amount) => {
    tabsScrollRef.current?.scrollBy({ left: amount, behavior: "smooth" });
  };

  // 6-step wizard navigation (SCRUM-468). Steps render one at a time instead
  // of the old stacked/collapsible sections. Every step is freely clickable
  // regardless of fill state (SCRUM-658); isWizardStepFilled only drives
  // the stepper's green/grey coloring.
  // Label line breaks match Figma's hard line breaks exactly (node ids
  // 5423:2337/2341/2345/2349/2353/2357) rather than relying on CSS auto-wrap,
  // since the button's fixed width doesn't match Figma's per-label text width.
  const WIZARD_STEPS = [
    { step: 1, labelLines: ["Chronic", "Conditions"] },
    { step: 2, labelLines: ["Medication", "Reconciliation"] },
    { step: 3, labelLines: ["Health", "Summary"] },
    { step: 4, labelLines: ["Problem List", "& Education"] },
    { step: 5, labelLines: ["Functional", "Assessment"] },
    { step: 6, labelLines: ["Careplan", "Notes"] },
  ];
  const [currentStep, setCurrentStep] = useState(1);

  const [carePlanCreated, setCarePlanCreated] = useState(false);
  const [careplanId, setCareplanId] = useState(undefined);
  const [creationDisabled, setCreationDisabled] = useState(false);
  const [isShareingModalOpen, setIsShareingModalOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [showAssignNurseModal, setShowAssignNurseModal] = useState(false);
  const [initialFormData, setInitialFormData] = useState(null);

  // form handeling
  const [formData, setFormData] = useState({});
  const [aiCreatedCareplan, setAiCreatedCareplan] = useState(false);
  const [aiCareplanloading, setAiCareplanloading] = useState(false);
  const [saveAiData, setSaveAiData] = useState(false);
  const [aiData, setAiData] = useState(false);
  const [isCareplanLoading, setisCareplanLoading] = useState(false);
  const [deletingConditions, setDeletingConditions] = useState([]);
  const [openTempId, setOpenTempId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // SCRUM-738: prior-month carryover — "prefill from last month" is a
  // manual, editor-side convenience (fetches the previous month's plan on
  // demand and copies its content into the current, already-created plan);
  // it does not touch the backend's create/save flow at all, so it can't
  // race with the create-careplan mutation's own query invalidation.
  const [isCarriedOver, setIsCarriedOver] = useState(false);
  const [carryoverLoading, setCarryoverLoading] = useState(false);

  // SCRUM-871/872: Copy Forward. The copy waits in pendingCopyForwardRef
  // until the current month's care plan is loaded (and created first, if
  // needed), then is merged into formData for the user to review and save.
  const [showCopyForwardModal, setShowCopyForwardModal] = useState(false);
  const pendingCopyForwardRef = useRef(null);
  // Bumped to re-run the apply effect when copying while already on the
  // current month (nothing else it depends on changes in that case).
  const [copyForwardTick, setCopyForwardTick] = useState(0);

  // Health Summary history popup: { metric, label } of the clicked field.
  const [healthHistoryField, setHealthHistoryField] = useState(null);

  // Physiological fields the user has edited since the plan loaded:
  // field -> "editing" | "blurred". The numbers-only rule applies only to
  // these; the inline error shows once the user leaves the field.
  const [physiologicalEdits, setPhysiologicalEdits] = useState({});


  const [statusAlertOpen, setStatusAlertOpen] = useState(false);
  const [statusAlertMessage, setStatusAlertMessage] = useState("");

  useEffect(() => {
    updateTabsScrollState();
    window.addEventListener("resize", updateTabsScrollState);
    return () => window.removeEventListener("resize", updateTabsScrollState);
  }, [formData?.chronic_conditions?.length]);


  const {
    data: carePlanData,
    isFetching: carePlan_loading,
    isError: carePlanError,
    refetch: refetchCarePLan,
  } = useQuery({
    queryKey: ["carePlan", selectedMonth, patient_id],
    queryFn: () => getCarePlan(selectedMonth?.month, selectedMonth?.year),
    enabled: isHistoricalOrCurrentMonth(selectedMonth, currentMonth, currentYear),
  });

  // Helper to check if careplan is finalized - prevents editing when finalized
  const isFinalized = carePlanData?.data?.status === "finalized";

  // "Completed" locks the wizard for editing same as finalized, but it's
  // reversible — toggling the status pill back to "In Progress"
  // (handleCareplanStatus below) unlocks it again.
  const isCareplanCompleted = carePlanData?.data?.status === "completed";

  // Drives the header's "View Only" pill + "Edit Careplan" button (SCRUM-...,
  // per Figma) — narrower than isReadOnly below, since PDF-imported plans,
  // patient viewers, and historical months are read-only for other reasons
  // and don't show this status-specific header treatment.
  const isViewOnlyStatus = isFinalized || isCareplanCompleted;

  // Helper to check if this careplan has a PDF document (read-only mode)
  const hasPdfDocument = carePlanData?.data?.careplan_document_url;
  const isPdfViewMode = !!hasPdfDocument;

  // Single source of truth for whether the loaded careplan can be edited.
  // Finalized plans, completed plans, patient viewers, and future months
  // are read-only: inputs reject changes at the source (readOnly / guarded
  // handlers) instead of accepting an edit and reverting it afterwards, so
  // the Update/Cancel bar never flashes in. Content stays fully readable.
  //
  // isPdfViewMode is deliberately NOT part of this — it only means "this
  // plan has an uploaded PDF" (drives the "View Uploaded Careplan" button),
  // not "this plan is locked forever." It used to be included here, which
  // meant reverting status from completed back to in_progress correctly
  // hid the Edit Careplan button / View Only pill but left the fields
  // permanently stuck read-only, since isPdfViewMode never changes with
  // status. Status (isFinalized / isCareplanCompleted) is what should gate
  // editability; a PDF being attached doesn't need to duplicate that lock.
  const isReadOnly =
    isFinalized ||
    isCareplanCompleted ||
    isPatient ||
    !isHistoricalOrCurrentMonth(selectedMonth, currentMonth, currentYear);

  useEffect(() => {
    // TODO: This is a temporary implementation for determining if a care plan exists for the current month due to urgency.
    // For better maintainability, please refactor this logic into a toggle hook to encapsulate and manage
    // the "current month careplan exists" check and use that check in overall app logic instead of calculating it here.
    if (
      selectedMonth?.month === currentMonth &&
      carePlanData?.data &&
      carePlanData?.data != []
    ) {
      setIsCurrentMonthCareplanExists(true);
    } else {
      setIsCurrentMonthCareplanExists(false);
    }
    // end

    if (!carePlanData) return;

    setCareplanId(carePlanData?.data?.id);
    sessionStorage.setItem("careplan_id", carePlanData?.data?.id);

    const newFormData = transformCareplanData(carePlanData);

    setFormData(newFormData);
    setInitialFormData(newFormData);
  }, [
    carePlanData,
    currentMonth,
    selectedMonth?.month,
    setIsCurrentMonthCareplanExists,
  ]);

  // Selecting a different month, or switching patients, is a new working
  // context: always land back on step 1. Every step is freely clickable
  // (SCRUM-658) regardless of fill state — isWizardStepFilled now only
  // drives the stepper's green/grey coloring, not whether a step can be
  // navigated to. Every step's content also always renders regardless of
  // isPdfViewMode now (fields can be blank for a PDF-imported plan, but the
  // step itself is never hidden) — previously steps 2-6 hid whenever
  // isPdfViewMode was true while step 1 didn't, so a currentStep left
  // stuck on another patient/month could land on a step that hid itself
  // with nothing else visible either, leaving a blank body. Resetting to
  // step 1 per patient/month is kept anyway as the sane default landing
  // spot.
  useEffect(() => {
    setCurrentStep(1);
  }, [selectedMonth?.month, selectedMonth?.year, patient_id, isPdfViewMode]);

  const { data: notes, isError } = useQuery(
    ["patient-notes", patient_id, "ccm_note", "created_at", location_id],
    () =>
      getPatientNotes(
        "ccm_note",
        "created_at",
        patient_id,
        location_id,
        isPatient
      ),
    {
      enabled: !!patient_id,
    }
  );

  const {
    data: conditionData,
    isLoading: condition_loading,
    refetch: refetchConditions,
  } = useQuery({
    queryKey: ["chronicConditionOptions", location_id],
    queryFn: () => getChronicConditionOptions(location_id),
  });

  const {
    data: drNotesConditions,
    isLoading: dr_note_condition_loading,
    refetch: refetchDrNoteConditions,
  } = useQuery({
    queryKey: ["drNoteConditions", patient_id],
    queryFn: () => getDrNoteConditions(patient_id, location_id),
    enabled: patient_id !== "undefined",
  });

  // Status changes (in_progress/completed/finalized) always operate on an
  // existing careplan, so PATCH it by id when one exists (current or
  // historical month) instead of POSTing to the create endpoint — the same
  // PATCH-vs-POST rule handleUpdateCareplan already follows for form saves.
  const mutation = useMutation(
    (data) =>
      careplanId
        ? updateHistoricalCarePlan(
          sessionStorage.getItem("acc_id"),
          location_id,
          careplanId,
          data
        )
        : addCarePlan(location_id, data),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries({ queryKey: ["carePlan"] });
        queryClient.invalidateQueries({ queryKey: ["notifications"] });
      },
      onError: (error) => {
        let errorMessage = "Could not change care plan status.";
        if (error?.response?.data) {
          if (
            Array.isArray(error.response.data.status) &&
            error.response.data.status.length > 0
          ) {
            errorMessage = error.response.data.status[0];
          } else if (typeof error.response.data.detail === "string") {
            errorMessage = error.response.data.detail;
          }
        }
        toast.error(errorMessage);
      },
    }
  );

  const deleteCareplanMutation = useMutation(() => deleteCarePlan(), {
    onSuccess: () => {
      setCareplanId(undefined)
      setCreationDisabled(false)
      setCarePlanCreated(false)
      setFormData({})
      setInitialFormData({})
      sessionStorage.setItem("plan_created", false)
      refetchCarePLan()
      // Restart Care Plan (as opposed to Restart Chat) actually completed —
      // tell the chatbot to reset its CCM endpoint mode back to /chatbot/ccm/chat.
      window.dispatchEvent(new Event("careplan_reset_success"));
      toast.success("Care Plan reset successfully.");
    },
    onError: (error) => {
      toast.error("Failed to reset care plan");
    },
  });

  useEffect(() => {
    const handleDeleteCareplan = () => {
      if (isHistoricalOrCurrentMonth(selectedMonth, currentMonth, currentYear)) {
        deleteCareplanMutation.mutate()
      }
    };

    window.addEventListener("delete_careplan_trigger", handleDeleteCareplan);

    return () => {
      window.removeEventListener("delete_careplan_trigger", handleDeleteCareplan);
    };
  }, []);

  const careplanMutation = useMutation(
    (data) => addCarePlan(location_id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["carePlan"], exact: false });
        queryClient.invalidateQueries(["patientInfo"]);
        queryClient.invalidateQueries({
          queryKey: ["patient-general-details"],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: ["patient-careplan-notes", patient_id],
          exact: false,
        });
      },
      onError: (error) => {
        toast.error("Failed to create careplan");
      },
    }
  );

  // Mutation used when editing/correcting a careplan for a PAST month.
  // Hits the PATCH-by-id endpoint instead of the "current careplan" endpoint
  // used by careplanMutation above.
  const historicalCareplanMutation = useMutation(
    (data) =>
      updateHistoricalCarePlan(
        sessionStorage.getItem("acc_id"),
        location_id,
        careplanId,
        data
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["carePlan"], exact: false });
        queryClient.invalidateQueries(["patientInfo"]);
        queryClient.invalidateQueries({
          queryKey: ["patient-general-details"],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: ["patient-careplan-notes", patient_id],
          exact: false,
        });
      },
      onError: () => {
        toast.error("Failed to update careplan");
      },
    }
  );

  const createNewCareplan = async () => {
    try {
      console.log("SUCCESSFULLY TRIGERED AT THIS POINT...! 3")
      await careplanMutation.mutateAsync({
        patient: patient_id,
        status: "in_progress",
      });
    } catch {
      toast.error("Failed to create careplan");
    }
  };

  // SCRUM-738 prior-month carryover: pulls the last care plan in on demand
  // and copies its content into the current (already-created) plan's form
  // state — purely client-side, nothing is saved until the user reviews and
  // hits the wizard's own Save. Called with no month/year (unlike the main
  // carePlanData query above, which always targets selectedMonth) so the
  // backend resolves "last" itself rather than the frontend computing a
  // specific calendar month. Reuses transformCareplanData's field mapping so
  // the copied shape always matches formData exactly, but strips each
  // condition's backend id (replacing it with a fresh tempId) so saving
  // creates new condition records for this month instead of patching
  // whichever month's records came back — carrying over identity, not just
  // content.
  const handlePrefillFromLastMonth = async () => {
    setCarryoverLoading(true);
    try {
      const priorCareplan = await getCarePlan();
      // TEMP DEBUG — remove once the "fetched but not prefilled" issue is
      // diagnosed. Shows the exact shape of the response with no month/year
      // filter, since that may differ from the single-record shape the main
      // (month/year-scoped) query normally returns.
      console.log("[Prefill from Last Month] raw response:", priorCareplan);

      if (!priorCareplan?.data) {
        toast.error("No care plan found for last month");
        return;
      }

      const carriedOverData = transformCareplanData(priorCareplan);
      console.log("[Prefill from Last Month] transformed:", carriedOverData);
      carriedOverData.chronic_conditions = (carriedOverData.chronic_conditions || []).map(
        (cond) => {
          const { id, ...rest } = cond;
          return { ...rest, tempId: `temp-${Date.now()}-${Math.floor(Math.random() * 10000)}` };
        }
      );

      setFormData(carriedOverData);
      setIsCarriedOver(true);
      toast.success("Prefilled from last month — review before saving");
    } catch (error) {
      console.error("[Prefill from Last Month] failed:", error);
      toast.error("Failed to load last month's care plan");
    } finally {
      setCarryoverLoading(false);
    }
  };

  useEffect(() => {
    refetchCarePLan();
  }, []);

  // useEffect(() => {
  //   console.log("SUCCESSFULLY TRIGERED AT THIS POINT...!")
  //   if (createPlan && !carePlanCreated) {
  //     console.log("SUCCESSFULLY TRIGERED AT THIS POINT...! 1")
  //     createNewCareplan();
  //     console.log("SUCCESSFULLY TRIGERED AT THIS POINT...! 2")
  //   }
  // }, [createPlan]);

  const handleNestedInputChange = (parent, key, value) => {
    setFormData((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [key]: value,
      },
    }));
  };

  const listToMultilineString = (values) => {
    if (!values) return "";
    if (Array.isArray(values)) {
      return values?.join("\n");
    }
    return values;
  };

  const multilineStringToList = (value) => {
    if (typeof value !== "string") return value;
    // Preserve every line the user typed, including blank ones — dropping
    // blank lines here (an earlier ?.filter(Boolean)) silently deleted real
    // points whenever one happened to be blank, since the surviving lines
    // then get renumbered on reload with no way to tell anything was lost.
    // The only line we intentionally drop is a single trailing blank line,
    // which is just the artifact of the last point ending in a newline.
    const lines = value.split("\n").map((item) => item?.trim());
    // A field with no text at all (e.g. only line breaks left behind after
    // backspacing it empty) is an empty list, same as clearing it with the
    // X - never [""].
    if (lines.every((line) => !line)) return [];
    if (lines.length && lines[lines.length - 1] === "") lines.pop();
    return lines;
  };

  const updateConditionField = (idOrTempId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      chronic_conditions: prev?.chronic_conditions?.map((cond) =>
        cond?.id === idOrTempId || cond?.tempId === idOrTempId
          ? { ...cond, [field]: value }
          : cond
      ),
    }));
  };

  const activeDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        openTempId &&
        activeDropdownRef.current &&
        !activeDropdownRef.current.contains(event.target)
      ) {
        setOpenTempId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openTempId]);

  const addedConditions =
    formData?.chronic_conditions?.map((cond) => cond.name?.toLowerCase()) || [];

  const filteredConditions = conditionData?.filter((med) => {
    return (
      med.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !addedConditions.includes(med.name.toLowerCase())
    );
  });

  // ICD-10 code -> condition name, built from the same catalog (conditionData,
  // "chronicConditionOptions") already used to populate a condition's fields
  // in the first place — no new data source (SCRUM-696). Used as a fallback
  // for conditions whose own `name` wasn't saved (e.g. older records).
  const icdNameByCode = useMemo(() => {
    const map = {};
    (conditionData || []).forEach((c) => {
      if (c?.icd_code) map[c.icd_code.trim().toUpperCase()] = c.name;
    });
    return map;
  }, [conditionData]);

  // Condition tab pill label (SCRUM-696): "<Condition Name> (<ICD-10 Code>)"
  // instead of the code on its own. Falls back to the icdNameByCode lookup
  // when a condition's own `name` is missing, and to the code alone (logged)
  // when no name can be resolved either way — a brand-new, still-blank
  // condition (no code yet) keeps the pre-existing "Condition N" placeholder.
  const getConditionChipLabel = (condition, index) => {
    if (!condition?.icd_code) return `Condition ${index + 1}`;
    const resolvedName =
      condition.name || icdNameByCode[condition.icd_code.trim().toUpperCase()];
    if (!resolvedName) {
      console.warn(
        `CarePlan: no condition name found for ICD-10 code "${condition.icd_code}" — showing code only.`
      );
      return condition.icd_code;
    }
    return `${resolvedName} (${condition.icd_code})`;
  };

  const filteredDrNotesConditions = drNotesConditions?.filter((med) => {
    return (
      med.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !addedConditions.includes(med.name.toLowerCase())
    );
  });

  // Which condition tab's detail form is showing (SCRUM-469). Falls back to
  // the first condition so there's always something active once any
  // condition exists, without needing a separate sync effect.
  const activeCondition =
    formData?.chronic_conditions?.find(
      (cond) => (cond.id ?? cond.tempId) === activeConditionKey
    ) || formData?.chronic_conditions?.[0];
  const activeConditionKeyResolved = activeCondition
    ? activeCondition.id ?? activeCondition.tempId
    : null;

  // SCRUM-749: prognosis reference options for the active condition, sourced
  // from its matching Chronic Condition Template's own prognosis text
  // (conditionData, the same catalog handlePopulateCondition already reads)
  // — matched by condition name, not by re-selecting the template. A
  // template's prognosis is written as one option per line (e.g. "Good:
  // ...\nFair: ...\nPoor: ..."), so each line becomes one option here with
  // no backend/data-model change needed. Each option can be copied, or
  // inserted — inserting only ever appends the option as a new line onto
  // whatever is already in Prognosis, so a Care Manager's existing edits are
  // never at risk of being replaced or cleared.
  const prognosisOptionsForActiveCondition = useMemo(() => {
    const activeName = (activeCondition?.name || "").trim().toLowerCase();
    if (!activeName) return [];
    const template = (conditionData || []).find(
      (c) => (c?.name || "").trim().toLowerCase() === activeName
    );
    if (!template?.prognosis) return [];
    return template.prognosis
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
  }, [activeCondition?.name, conditionData]);

  const handleAddCondition = () => {
    const tempId = `temp-${Date.now()}-${Math.floor(Math.random() * 10000)}`;

    setFormData((prev) => ({
      ...prev,
      chronic_conditions: [
        ...prev?.chronic_conditions,
        {
          tempId,
          name: "",
          prognosis: "",
          symptoms: "",
          treatment_goals: "",
          planned_interventions: "",
          icd_code: "",
          for_billing_icd: false
        },
      ],
    }));

    // Switch focus to the new blank tab immediately (SCRUM-469).
    setActiveConditionKey(tempId);
  };

  const handleDeleteCondition = (condition, index) => {
    if (condition?.id) {
      setDeletingConditions((prev) => [...prev, condition?.id]);
    }

    const deletedKey = condition.id || condition.tempId;

    setFormData((prev) => {
      const remaining = prev?.chronic_conditions?.filter((c, i) => i !== index);

      // If the deleted tab was active, fall back to a neighboring tab (or
      // none, if that was the last condition) so the view isn't left
      // pointing at a condition that no longer exists.
      if (activeConditionKey === deletedKey) {
        const fallbackIndex = Math.min(index, remaining.length - 1);
        const fallback = remaining[fallbackIndex];
        setActiveConditionKey(fallback ? fallback.id || fallback.tempId : null);
      }

      return { ...prev, chronic_conditions: remaining };
    });
  };

  // Populates a condition tab's fields from the canned-conditions list.
  // Works for both a brand-new tab (matched by tempId) and an already-saved
  // one (matched by id) — SCRUM-469 always shows this selector, not just for
  // new conditions.
  const handlePopulateCondition = (filteredCondition, conditionKey) => {
    setFormData((prev) => ({
      ...prev,
      chronic_conditions: prev.chronic_conditions.map((cond) =>
        (cond.id ?? cond.tempId) === conditionKey
          ? {
            ...cond,
            name: filteredCondition?.name || "",
            prognosis: filteredCondition?.prognosis || "",
            symptoms: filteredCondition?.symptoms || "",
            treatment_goals: filteredCondition?.treatment_goals || "",
            planned_interventions:
              filteredCondition?.planned_interventions || "",
            icd_code: filteredCondition?.icd_code || "",
            for_billing_icd: false
          }
          : cond
      ),
    }));

    setOpenTempId(null);
  };

  useEffect(() => {
    const fetchAiCareplan = async (event) => {
      const includeRecording = event.detail?.includeRecording !== false; // Default true if undefined

      setAiCareplanloading(true);
      if (carePlanData && carePlanData?.data == []) {
        await createNewCareplan();
      }
      const ccm_session_id = sessionStorage.getItem("ccm_session_id");

      // Passing the includeRecording parameter to the service call
      const ai_care_paln_data = await getAiCarePlan(
        ccm_session_id,
        includeRecording
      );

      // Checking for specific recording statuses
      const recordingStatus = ai_care_paln_data?.recording_status?.status;
      const processingSource =
        ai_care_paln_data?.recording_status?.processing_source;

      // Only showing "no recording" alert if we DID NOT explicitly ask for chat only
      if (
        recordingStatus === "no_recording" &&
        processingSource !== "chat_only"
      ) {
        setStatusAlertMessage(
          "The most recent RingCentral call with the patient was not recorded. Use the chat conversation to generate the Care Plan."
        );
        setStatusAlertOpen(true);
        setAiCareplanloading(false);
        return;
      } else if (recordingStatus === "recording_processing") {
        setStatusAlertMessage(
          "Your RingCentral call recording is still being processed and saved to our database. Please wait 1–2 minutes before attempting to regenerate."
        );
        setStatusAlertOpen(true);
        setAiCareplanloading(false);
        return;
      } else if (recordingStatus === "call_active") {
        setStatusAlertMessage(
          "The RingCentral call with the patient is still in progress. Please end the call before generating the Care Plan."
        );
        setStatusAlertOpen(true);
        setAiCareplanloading(false);
        return;
      } else if (recordingStatus === "voicemail") {
        setStatusAlertMessage(
          "The most recent RingCentral call was a voicemail. Please generate care plan using the chat conversation only."
        );
        setStatusAlertOpen(true);
        setAiCareplanloading(false);
        return;
      }

      // Dispatch event to tell Chatbot to clear conversation history
      window.dispatchEvent(new Event("careplan_generation_success"));

      setAiData(ai_care_paln_data);
      setAiCreatedCareplan(true);
      sessionStorage.removeItem("ccm_session_id");
      setAiCareplanloading(false);
      setSaveAiData(true);
    };

    const CreateAiCareplan = (event) => {
      fetchAiCareplan(event);
    };

    window.addEventListener("create_careplan_with_chatbot", CreateAiCareplan);

    return () => {
      window.removeEventListener(
        "create_careplan_with_chatbot",
        CreateAiCareplan
      );
    };
  }, []);

  const normalizeChronicConditions = (conditions = []) => {
    return conditions?.map((cond) => {
      const fieldsToNormalize = [
        "prognosis",
        "symptoms",
        "treatment_goals",
        "planned_interventions",
      ];

      const normalized = { ...cond };

      fieldsToNormalize?.forEach((field) => {
        if (Array.isArray(normalized[field])) {
          normalized[field] = normalized[field]?.join("\n");
        } else if (normalized[field] == null) {
          normalized[field] = "";
        }
      });

      return normalized;
    });
  };

  const transformCareplanData = (careplan_data) => {
    if (selectedMonth?.month === currentMonth) {
      if (careplan_data && careplan_data?.data != []) {
        setCarePlanCreated(true);
        sessionStorage.setItem("plan_created", true);
      } else {
        setCarePlanCreated(false);
        sessionStorage.setItem("plan_created", false);
      }
    }
    window.dispatchEvent(new Event("plan_creation_updated"));

    const data = careplan_data?.data;

    const newFormData = {
      patient: patient_id,
      complex: data?.complex || false,
      physiological_data: {
        weight: data?.physiological_data?.weight || "",
        bmi: data?.physiological_data?.bmi || "",
        height: data?.physiological_data?.height || "",
        blood_glucose: data?.physiological_data?.blood_glucose || "",
        blood_pressure: data?.physiological_data?.blood_pressure || "",
      },
      behavioral_data: {
        sleep: data?.behavioral_data?.sleep || "",
        exercise: data?.behavioral_data?.exercise || "",
        water_diet: data?.behavioral_data?.water_diet || "",
      },
      dob_confirmed: data?.dob_confirmed || false,
      has_chronic_conditions: data?.has_chronic_conditions || false,
      consent_for_calls: data?.consent_for_calls || false,
      activities_of_daily_living:
        listToMultilineString(data?.activities_of_daily_living) || "",
      caregiver_assessment:
        listToMultilineString(data?.caregiver_assessment) || "",
      reported_side_effect:
        listToMultilineString(data?.reported_side_effect) || "",
      allergies: listToMultilineString(data?.allergies) || "",
      pmhx: listToMultilineString(data?.pmhx) || "",
      potential_barriers: listToMultilineString(data?.potential_barriers) || "",
      recommendations: listToMultilineString(data?.recommendations) || "",
      recent_hospitalization:
        listToMultilineString(data?.recent_hospitalization) || "",
      // Prognosis/Symptoms/Treatment Goals/Planned Intervention come back
      // from the API as arrays, same as the other list fields above — must
      // be normalized to newline-joined strings before reaching
      // FreeTextSection/SymptomTriStateList, which both expect a plain
      // string value.
      chronic_conditions: normalizeChronicConditions(data?.chronic_conditions) || [],
      medications: [
        {
          id: data?.medications?.id || null,
          name: data?.medications?.name || "",
        },
      ],
      note: data?.note || "",
    };

    return newFormData;
  };

  useEffect(() => {
    if (selectedMonth?.month === currentMonth) {
      if (carePlanData && carePlanData?.data != []) {
        setCarePlanCreated(true);
        sessionStorage.setItem("plan_created", true);
      } else {
        setCarePlanCreated(false);
        sessionStorage.setItem("plan_created", false);
      }
    }
    window.dispatchEvent(new Event("plan_creation_updated"));

    if (aiData) {
      const data = aiData?.notes;
      const isUpdated = !!data?.delete_conditions;

      if (isUpdated) {
        setDeletingConditions(data?.delete_conditions);
      }

      const normalize = normalizeChronicConditions(data?.chronic_conditions) || [];

      // filter out deleted conditions from previous conditions
      let previousConditions = formData?.chronic_conditions || [];

      if (isUpdated && Array.isArray(data?.delete_conditions)) {
        previousConditions = previousConditions?.filter(
          (cond) => !data?.delete_conditions?.includes(cond?.id)
        );
      }

      // build final chronic conditions for payload
      const chronic_conditions = isUpdated
        ? [...previousConditions, ...normalize]
        : normalize;

      // Destructuring form data
      const newFormData = {
        patient: patient_id,
        ...(carePlanData?.data?.status === undefined && { status: "in_progress" }),
        complex: formData?.complex || false,
        physiological_data: {
          weight: data?.physiological_data?.weight || "",
          bmi: data?.physiological_data?.bmi || "",
          height: data?.physiological_data?.height || "",
          blood_glucose: data?.physiological_data?.blood_glucose || "",
          blood_pressure: data?.physiological_data?.blood_pressure || "",
        },
        behavioral_data: {
          sleep: data?.behavioral_data?.sleep || "",
          exercise: data?.behavioral_data?.exercise || "",
          water_diet: data?.behavioral_data?.water_diet || "",
        },
        dob_confirmed: data?.dob_confirmed || false,
        has_chronic_conditions: data?.has_chronic_conditions || false,
        consent_for_calls: data?.consent_for_calls || false,
        activities_of_daily_living:
          data?.activities_of_daily_living || [],
        caregiver_assessment:
          data?.caregiver_assessment || [],
        reported_side_effect:
          data?.reported_side_effect || [],
        allergies: data?.allergies || [],
        pmhx: data?.pmhx || [],
        potential_barriers:
          data?.potential_barriers || [],
        recommendations: data?.recommendations || [],
        recent_hospitalization:
          data?.recent_hospitalization || [],
        chronic_conditions: chronic_conditions,
        medications: [
          {
            id: data?.medications[0]?.id || null,
            name:
              careplanMode === "new"
                ? listToMultilineString(data?.medications)
                : data?.medications[0]?.name || "",
          },
        ],
        note: data?.notes || "",
        delete_conditions: data?.delete_conditions || [],
      };

      handleUpdateCareplan(newFormData);
    }
  }, [carePlanData, aiCreatedCareplan, careplanMode]);

  useEffect(() => {
    setPhysiologicalEdits({});
  }, [carePlanData]);

  useEffect(() => {
    // Read-only plans can't be modified (inputs block edits at the source
    // via the readOnly props below), so just make sure no stale edit from a
    // previously-editable month lingers in formData.
    if (isReadOnly) {
      setFormData(initialFormData);
      setDeletingConditions([]);
    }
  }, [isReadOnly, initialFormData]);

  const handleUpdateCareplan = async (overrideData = null, { silent = false } = {}) => {
    setisCareplanLoading(true);

    const dataToSubmit = overrideData ?? formData;
    const delete_conditions = overrideData?.delete_conditions ?? deletingConditions;

    const conversionFields = {
      activities_of_daily_living:
        multilineStringToList(formData?.activities_of_daily_living) || "",
      caregiver_assessment:
        multilineStringToList(formData?.caregiver_assessment) || "",
      reported_side_effect:
        multilineStringToList(formData?.reported_side_effect) || "",
      allergies: multilineStringToList(formData?.allergies) || "",
      pmhx: multilineStringToList(formData?.pmhx) || "",
      potential_barriers:
        multilineStringToList(formData?.potential_barriers) || "",
      recommendations:
        multilineStringToList(formData?.recommendations) || "",
      recent_hospitalization:
        multilineStringToList(formData?.recent_hospitalization) || "",
    };

    const basePayload = {
      ...dataToSubmit,
      chronic_conditions: dataToSubmit?.chronic_conditions?.map((cond) => {
        const allowed = [
          "id",
          "name",
          "prognosis",
          "symptoms",
          "treatment_goals",
          "planned_interventions",
          "icd_code",
          "for_billing_icd",
        ];

        return allowed.reduce((acc, key) => {
          if (cond[key] !== undefined && cond[key] !== null) {
            if (key === "id" && cond[key] == null) return acc;
            acc[key] = cond[key];
          }
          return acc;
        }, {});
      }),
      delete_conditions: delete_conditions,
    };

    const finalFormData = overrideData
      ? basePayload
      : { ...basePayload, ...conversionFields };

    setDeletingConditions([]);

    // Saving an edit to an existing careplan (current month or a past month)
    // always goes through the PATCH-by-id endpoint (historicalCareplanMutation).
    // careplanMutation (POST) is only used to create a brand-new current-month
    // careplan (e.g. the first Toca Copilot-generated plan, before any record/id
    // exists yet).
    const isNewCurrentMonthCareplan =
      !careplanId &&
      selectedMonth?.month === currentMonth &&
      selectedMonth?.year === currentYear;

    try {
      if (careplanId) {
        await historicalCareplanMutation.mutateAsync(finalFormData);
      } else if (isNewCurrentMonthCareplan) {
        await careplanMutation.mutateAsync(finalFormData);
      } else {
        toast.error("No care plan found for this month to update.");
        setisCareplanLoading(false);
        return;
      }
      // SCRUM-619: on the final wizard step this save is immediately followed
      // by handleCareplanStatus (see handleWizardContinue below), which
      // surfaces its own single toast/modal for that call's outcome — e.g.
      // "Please first assign nurse to the patient." if no nurse is assigned.
      // Showing this generic success toast too would stack two popups for
      // one button click, so it's skipped when silent.
      if (!silent) {
        toast.success(`CarePlan updated successfully.`);
      }
    } catch (error) {
      toast.error("Failed to update CarePlan, Something went wrong.");
      setisCareplanLoading(false);
    }

    setAiData(null);
    setAiCreatedCareplan(false);
    setAiCareplanloading(false);
    setisCareplanLoading(false);
  };

  // Wizard step navigation (SCRUM-468).
  const handleWizardBack = () => {
    setCurrentStep((step) => Math.max(1, step - 1));
  };

  // Every step is directly reachable at any time (SCRUM-658) — no longer
  // gated on isWizardStepFilled, so the user can freely jump forward or
  // backward across sub-pages without filling them in order. Data already
  // entered on any step lives in formData and is unaffected by which step
  // is currently shown, so nothing is lost when jumping away and back.
  // Confirm/Finalize-time validation (handleWizardContinue /
  // handleUpdateCareplan) is unchanged and still enforces completeness.
  const handleWizardStepClick = (step) => {
    setCurrentStep(step);
  };

  // Save & Continue / Update & Continue / Confirm Careplan — every step reuses
  // the exact same save call as the rest of the form (handleUpdateCareplan),
  // then advances to the next step. No new save endpoint per step. On the
  // final step ("Confirm Careplan"), this also completes the careplan's
  // status via the exact same action as clicking the status pill
  // (handleCareplanStatus) — that's what unlocks the Assign Nurse modal and
  // switches the wizard into its read-only "View Next" state.
  // Character limits for every FreeTextSection field - Careplan Notes gets a
  // larger cap than the rest. FreeTextSection blocks typing at the cap, but a
  // paste or template insert can still push a field over it, so every field
  // is also validated here: over-limit fields show an inline error and
  // Save/Update & Continue stays disabled until they're trimmed. Checked
  // across all steps/conditions (not just the visible one), since every
  // step's save sends the whole form.
  const FREE_TEXT_MAX_LENGTH = 5000;
  const CAREPLAN_NOTES_MAX_LENGTH = 65000;
  const isOverCharLimit = (value, max) => (value || "").length > max;
  const usesLegacySymptomsField = !wasCreatedOnOrAfterSymptomsCutoff(carePlanData?.data?.created_at);
  const charLimitErrorFields = [
    ...(formData?.chronic_conditions || []).flatMap((cond) => {
      const name = cond?.name || cond?.icd_code || "Condition";
      return [
        ["Prognosis", cond?.prognosis],
        ...(usesLegacySymptomsField ? [["Symptoms", cond?.symptoms]] : []),
        ["Treatment Goals", cond?.treatment_goals],
        ["Planned Intervention", cond?.planned_interventions],
      ]
        .filter(([, value]) => isOverCharLimit(value, FREE_TEXT_MAX_LENGTH))
        .map(([label]) => `${label} (${name})`);
    }),
    ...[
      ["Reported Side Effect", formData?.reported_side_effect],
      ["Allergies", formData?.allergies],
      ["Medications", formData?.medications?.[0]?.name],
      ["Sleep", formData?.behavioral_data?.sleep],
      ["Diet/Water", formData?.behavioral_data?.water_diet],
      ["Exercise", formData?.behavioral_data?.exercise],
      ["Past Medical History", formData?.pmhx],
      ["Potential Barriers", formData?.potential_barriers],
      ["Recommendations", formData?.recommendations],
      ["Recent Hospitalization", formData?.recent_hospitalization],
      ["Activities of Daily Living", formData?.activities_of_daily_living],
      ["Caregiver Assessment", formData?.caregiver_assessment],
    ]
      .filter(([, value]) => isOverCharLimit(value, FREE_TEXT_MAX_LENGTH))
      .map(([label]) => label),
    ...(isOverCharLimit(formData?.note, CAREPLAN_NOTES_MAX_LENGTH) ? ["Careplan Notes"] : []),
  ];
  const hasCharLimitErrors = charLimitErrorFields.length > 0;

  const PHYSIOLOGICAL_FIELD_LABELS = {
    weight: "Weight",
    blood_glucose: "Blood Glucose",
    bmi: "BMI",
    blood_pressure: "Blood Pressure",
    height: "Height",
  };
  const physiologicalErrorFields = Object.keys(physiologicalEdits).filter(
    (field) => !isValidPhysiologicalValue(field, formData?.physiological_data?.[field] || "")
  );
  const hasPhysiologicalErrors = physiologicalErrorFields.length > 0;

  const handleWizardContinue = async () => {
    if (hasCharLimitErrors || hasPhysiologicalErrors) return;
    const isFinalStep = currentStep === WIZARD_STEPS.length;
    await handleUpdateCareplan(null, { silent: isFinalStep });
    if (isFinalStep) {
      handleCareplanStatus(carePlanData?.data?.status);
    }
    setCurrentStep((step) => Math.min(WIZARD_STEPS.length, step + 1));
  };

  // Whether a given wizard step actually has data saved — drives only the
  // stepper's green/grey coloring (SCRUM-658 made every step clickable
  // regardless of this).
  const hasText = (value) => Boolean(value && (Array.isArray(value) ? value.length : String(value).trim().length));
  const isWizardStepFilled = (step) => {
    switch (step) {
      case 1:
        return Boolean(formData?.chronic_conditions?.length);
      case 2:
        return (
          hasText(formData?.reported_side_effect) ||
          hasText(formData?.allergies) ||
          hasText(formData?.medications?.[0]?.name)
        );
      case 3:
        return (
          Object.values(formData?.physiological_data || {}).some(hasText) ||
          Object.values(formData?.behavioral_data || {}).some(hasText)
        );
      case 4:
        return (
          hasText(formData?.pmhx) ||
          hasText(formData?.potential_barriers) ||
          hasText(formData?.recommendations) ||
          hasText(formData?.recent_hospitalization)
        );
      case 5:
        return hasText(formData?.activities_of_daily_living) || hasText(formData?.caregiver_assessment);
      case 6:
        return hasText(formData?.note);
      default:
        return false;
    }
  };

  const handleBillingCheckBox = (id) => {
    setFormData((prev) => {
      const currentBillingCount = prev.chronic_conditions.filter(
        (cond) => cond.for_billing_icd
      ).length;

      return {
        ...prev,
        chronic_conditions: prev.chronic_conditions.map((cond) => {
          const conditionKey = cond.id ?? cond.tempId;
          // If this is the clicked checkbox
          if (conditionKey === id) {
            // If already checked, allow uncheck
            if (cond.for_billing_icd) return { ...cond, for_billing_icd: false };

            // If less than 2 selected, allow check
            if (currentBillingCount < 2) return { ...cond, for_billing_icd: true };

            // If already 2 selected, do not allow more
            toast.error("Only two conditions can be selected for billing.");
            return cond;
          }
          return cond;
        }),
      };
    });
  };

  // "Include Condition" popup (per Figma) — opened instead of the plain
  // reject-toast whenever the user tries to include a THIRD condition while
  // two are already billed. Lets them see all conditions at once and swap
  // which two are included, rather than only being able to uncheck one
  // first and retry. Draft state here is local until Save is pressed;
  // Cancel discards it. Checking/unchecking a single condition directly
  // (not hitting the 2-max) still uses handleBillingCheckBox above as-is.
  const [showIncludeConditionModal, setShowIncludeConditionModal] = useState(false);
  const [includeDraft, setIncludeDraft] = useState({});

  const openIncludeConditionModal = () => {
    const draft = {};
    formData?.chronic_conditions?.forEach((cond) => {
      draft[cond.id ?? cond.tempId] = Boolean(cond.for_billing_icd);
    });
    setIncludeDraft(draft);
    setShowIncludeConditionModal(true);
  };

  const handleIncludeDraftToggle = (key) => {
    setIncludeDraft((prev) => {
      if (!prev[key]) {
        const checkedCount = Object.values(prev).filter(Boolean).length;
        if (checkedCount >= 2) {
          toast.error("Only two conditions can be selected for billing.");
          return prev;
        }
      }
      return { ...prev, [key]: !prev[key] };
    });
  };

  const handleSaveIncludeConditions = () => {
    setFormData((prev) => ({
      ...prev,
      chronic_conditions: prev.chronic_conditions.map((cond) => ({
        ...cond,
        for_billing_icd: Boolean(includeDraft[cond.id ?? cond.tempId]),
      })),
    }));
    setShowIncludeConditionModal(false);
  };

  const {
    data: patient,
    error,
    isLoading,
    refetch,
  } = useQuery(
    ["patient-general-details", patient_id],
    () => getPatientGeneralDetails(patient_id),
    { enabled: !!patient_id }
  );

  useEffect(() => {
    setCareplanId(carePlanData?.data?.id);
    sessionStorage.setItem("careplan_id", carePlanData?.data?.id);

    // Pending condition deletions belong to the month they were made in.
    setDeletingConditions([]);

    if (selectedMonth?.month != currentMonth) {
      refetchCarePLan();
      sessionStorage.setItem("isCurrentMonth", false);
    } else {
      sessionStorage.setItem("isCurrentMonth", true);
    }

    window.dispatchEvent(new Event("month_change_update"));
  }, [selectedMonth?.month]);

  const monthName = new Date(selectedMonth.year, selectedMonth.month - 1).toLocaleString('en-US', { month: 'long' });
  const newDate = `${monthName}, ${selectedMonth.year}`;
  const firstName = patient?.user?.first_name?.replace(/\s+/g, '_') || 'Unknown';
  const lastName = patient?.user?.last_name?.replace(/\s+/g, '_') || 'Unknown';
  const subject_name = `${selectedMonth.month}_${selectedMonth.year}_${firstName}_${lastName}_Care_Plan`;

  const shareAuditReportMutation = useMutation(
    ({ file, provider }) => {
      return shareAuditReport(file, provider, null, newDate, subject_name);
    },
    {
      onMutate: () => {
        const toastId = toast.loading("Sending care plan...");
        return { toastId };
      },
      onSuccess: (_, __, context) => {
        toast.success("Care plan shared successfully.", { id: context?.toastId });
        setIsShareingModalOpen(false);
        setSharing(false);
      },
      onError: (error, __, context) => {
        console.error("Failed to share care plan:", error);
        setSharing(false);
        if (error?.response?.status === 413) {
          toast.error("PDF file too large. Try with fewer pages or contact support.", { id: context?.toastId });
        } else {
          toast.error("Failed to share care plan. Please try again.", { id: context?.toastId });
        }
      },
    }
  );

  const textareaRefs = useRef({});

  useEffect(() => {
    Object.values(textareaRefs.current).forEach((textarea) => {
      if (textarea) {
        textarea.style.height = "auto";
        textarea.style.height = textarea.scrollHeight + "px";
      }
    });
  }, [formData]);

  useLayoutEffect(() => {
    Object.values(textareaRefs.current).forEach((textarea) => {
      if (textarea) {
        textarea.style.height = "auto";
        textarea.style.height = textarea.scrollHeight + "px";
      }
    });
  });

  const handleTextAreaInput = (e) => {
    const textarea = e.target;
    textarea.style.height = "auto";
    textarea.style.height = textarea.scrollHeight + "px";
  };

  const handleCareplanStatus = (currentStatus) => {
    if (currentStatus === "in_progress") {
      mutation.mutate(
        {
          patient: patient_id,
          status: "completed",
        },
        {
          // Assign Nurse is a Case Manager/Admin step — a nurse reopening
          // a plan (from Finalized or Completed) and re-completing it
          // herself doesn't need to be prompted to assign a nurse.
          onSuccess: () => {
            if (!isNurseDashboard) setShowAssignNurseModal(true);
          },
        }
      );
    } else if (currentStatus === "completed") {
      mutation.mutate({
        patient: patient_id,
        status: "in_progress",
      });
    } else if (currentStatus === "finalized") {
      // Reopening a finalized plan for editing — reachable via the "Edit
      // Careplan" button, which only nurses see while finalized.
      mutation.mutate({
        patient: patient_id,
        status: "in_progress",
      });
    }
  };

  const handleAssignNurse = () => {
    mutation.mutate({
      patient: patient_id,
      status: "completed",
    });
  };

  const handleCareplan = async (type) => {
    if (type === "manual") {
      await createNewCareplan();
    }
    if (type === "copilot") {
      onStartTocaCopilot?.();
    }
    setCreationDisabled(true);
  };

  // SCRUM-871/872 Copy Forward: merges the selected sections/conditions of
  // another month's care plan into the current month's form - purely
  // client-side like handlePrefillFromLastMonth, so nothing is saved until
  // the user reviews and hits the wizard's own Save. What is your meaning you got experience to the d restaurant this morning of stuff in AWS also we have to do all the things on the results of Each checked field
  // (a formData path, see COPY_FORWARD_SECTIONS) replaces the current value;
  // a copied condition replaces the current one with the
  // same ICD code (keeping its id and billing flag), otherwise it's added as
  // a new condition (fresh tempId, so saving creates it for this month).
  const applyCopyForward = ({ sourceLabel, sourceData, fields, conditionIndexes }) => {
    const source = transformCareplanData({ data: sourceData });
    const newTempId = (i) => `temp-${Date.now()}-${i}-${Math.floor(Math.random() * 10000)}`;

    setFormData((prev) => {
      const next = { ...prev };
      fields.forEach((path) => {
        if (path === "medications") {
          // Keep the current month's medication record id, copy only the text.
          next.medications = [{ id: prev?.medications?.[0]?.id ?? null, name: source.medications?.[0]?.name || "" }];
          return;
        }
        const [group, key] = path.split(".");
        if (key) {
          next[group] = { ...(next[group] || {}), [key]: source[group]?.[key] ?? "" };
        } else {
          next[group] = source[group];
        }
      });

      if (conditionIndexes.length > 0) {
        const conditions = [...(prev?.chronic_conditions || [])];
        conditionIndexes.forEach((sourceIndex, i) => {
          const { id, tempId, ...copied } = source.chronic_conditions[sourceIndex] || {};
          const match = conditions.findIndex(
            (cond) => copied.icd_code && cond.icd_code === copied.icd_code
          );
          if (match >= 0) {
            conditions[match] = {
              ...conditions[match],
              name: copied.name,
              prognosis: copied.prognosis,
              symptoms: copied.symptoms,
              treatment_goals: copied.treatment_goals,
              planned_interventions: copied.planned_interventions,
            };
          } else {
            // Max two billed codes at a time - a copied billed code only
            // stays billed while there's room for it.
            const billedCount = conditions.filter((cond) => cond.for_billing_icd).length;
            conditions.push({
              ...copied,
              for_billing_icd: Boolean(copied.for_billing_icd) && billedCount < 2,
              tempId: newTempId(i),
            });
          }
        });
        next.chronic_conditions = conditions;
      }
      return next;
    });

    const currentLabel = `${monthNames[currentMonth - 1]} ${currentYear}`;
    toast.success(`${sourceLabel} care plan details have been copied to ${currentLabel}. Review and save to keep them.`);
  };

  // The current month's care plan, for Copy Forward's "already has data"
  // warnings: the live form (including unsaved edits) when it's on screen,
  // otherwise fetched while the modal is open. Same query key shape as the
  // main carePlan query, so it shares that cache.
  const isViewingCurrentMonth =
    selectedMonth?.month === currentMonth && selectedMonth?.year === currentYear;
  const { data: currentMonthCareplan } = useQuery({
    queryKey: ["carePlan", { month: currentMonth, year: currentYear }, patient_id],
    queryFn: () => getCarePlan(currentMonth, currentYear),
    enabled: showCopyForwardModal && !isViewingCurrentMonth,
  });
  const copyForwardCurrentData = isViewingCurrentMonth ? formData : currentMonthCareplan?.data;

  const handleCopyForward = (copy) => {
    pendingCopyForwardRef.current = copy;
    const isViewingCurrentMonth =
      selectedMonth?.month === currentMonth && selectedMonth?.year === currentYear;
    if (!isViewingCurrentMonth) {
      // The effect below applies the copy once the current month loads.
      setSelectedMonth({ month: currentMonth, year: currentYear });
    } else {
      setCopyForwardTick((tick) => tick + 1);
    }
  };

  useEffect(() => {
    const copy = pendingCopyForwardRef.current;
    if (!copy || carePlan_loading) return;
    if (!(selectedMonth?.month === currentMonth && selectedMonth?.year === currentYear)) return;

    // No current-month care plan yet: create it (same as Create CarePlan >
    // Manual), then this effect runs again once it has loaded.
    if (!carePlanData?.data?.id) {
      if (!copy.creatingCareplan) {
        copy.creatingCareplan = true;
        handleCareplan("manual");
      }
      return;
    }

    pendingCopyForwardRef.current = null;
    const status = carePlanData?.data?.status;
    if (status === "completed" || status === "finalized") {
      toast.error(
        `This month's care plan is ${status === "completed" ? "Completed" : "Finalized"}. Click "Edit Careplan" to reopen it, then copy again.`
      );
      return;
    }
    applyCopyForward(copy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carePlanData, carePlan_loading, selectedMonth, copyForwardTick]);

  // Downloads the care plan. If a finalized PDF document already exists
  // (careplan_document_url), open it in a new tab (same as View PDF)
  // instead of navigating to the finalize-careplan page.
  const handleCareplanDownload = () => {
    const pdfUrl = carePlanData?.data?.careplan_document_url;

    if (pdfUrl) {
      try {
        window.open(pdfUrl, "_blank", "noopener,noreferrer");
      } catch (error) {
        console.error("Failed to open PDF document:", error);
        toast.error("Failed to open PDF document");
      }
      return;
    }

    const newTabUrl = `/patient/finalize-careplan?month=${selectedMonth?.month}&year=${selectedMonth?.year}`;
    window.open(newTabUrl, "_blank");
  };

  // Opens the generated PDF document (careplan_document_url) in a new tab.
  // Validates the URL exists first and guards window.open in a try/catch
  // in case the browser blocks the popup or the call otherwise fails.
  const handleViewPdf = () => {
    const url = carePlanData?.data?.careplan_document_url;

    if (!url) {
      toast.error("PDF document not available");
      return;
    }

    try {
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Failed to open PDF document:", error);
      toast.error("Failed to open PDF document");
    }
  };

  const generateKeyValuePairs = (data) => {
    return Object.entries(data)?.map(([key, value]) => ({
      key: key?.replace(/_/g, " ")?.replace(/\b\w/g, (c) => c?.toUpperCase()),
      value: Array.isArray(value) ? value?.join(", ") : value || "_",
    }));
  };

  const handleCareplanSharing = async (provider) => {
    setSharing(true);

    // Filter chronic conditions to show only those marked for billing
    const billedConditions = (carePlanData?.data?.chronic_conditions || []).filter(
      (cond) => cond?.for_billing_icd === true
    );

    // Generate report date as "Month Year" format
    const reportDate = new Date(selectedMonth.year, selectedMonth.month - 1)
      .toLocaleString('en-US', { month: 'long', year: 'numeric' });

    // Generate PDF using the new PDF generator
    try {
      const pdfDoc = await generateFinalizeCareplanPDF({
        patient,
        carePlanData: carePlanData?.data,
        conditions: billedConditions,
        medications: carePlanData?.data?.medications,
        reportDate,
        logo,
      });

      // Generate blob and send to backend (jsPDF uses output(), not getBlob())
      const blob = pdfDoc.output("blob");
      const emails = [provider?.externalProvider, provider?.provider].filter(Boolean);
      const jsonEmails = JSON.stringify(emails);
      shareAuditReportMutation.mutate({ file: blob, provider: jsonEmails });
    } catch (error) {
      console.error("Failed to generate care plan PDF:", error);
      toast.error("Failed to generate care plan PDF");
      setSharing(false);
    }
    // Note: setSharing(false) is handled in mutation's onSuccess/onError callbacks
  };

  // month year dropdown functions
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const options = [];
  let startMonth = currentMonth;
  let startYear = currentYear - 1;

  for (let i = 0; i < 13; i++) {
    options.push({ month: startMonth, year: startYear });
    startMonth++;

    if (startMonth > 12) {
      startMonth = 1;
      startYear++;
    }
  }

  const handleMonthChange = (value) => {
    const [month, year] = value.split("-");
    setSelectedMonth({ month: Number(month), year: Number(year) });
  };

  const last12Months = useMemo(() => {
    const today = new Date();
    const arr = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      arr.push({
        month: d.getMonth() + 1,
        year: d.getFullYear(),
      });
    }
    return arr;
  }, []);

  // The yearly-document-status endpoint already returns the full 12-month
  // window for the requested year, so a single request for the current year
  // covers every month in the dropdown (including previous-year months).
  const { data: documentStatusData } = useQuery({
    queryKey: ["careplanYearlyDocumentStatus", patient_id, currentYear],
    queryFn: () => getYearlyDocumentStatus(currentYear),
    enabled: !!patient_id,
  });

  // Maps "month-year" -> care_plan_type ("pdf" | "manual" | "none") for the month dropdown icons.
  // Entries carry no `year` field, only a label like "August - 2025", so the
  // year is parsed from the label; if that fails, it's inferred from the
  // rolling 12-month window (a month after the current one belongs to last year).
  const monthDocumentStatusMap = useMemo(() => {
    const map = {};
    documentStatusData?.data?.forEach((entry) => {
      const labelYear = Number(entry.label?.split(" - ")[1]);
      const entryYear =
        labelYear ||
        (entry.month > currentMonth ? currentYear - 1 : currentYear);
      map[`${entry.month}-${entryYear}`] = entry.care_plan_type;
    });
    return map;
  }, [documentStatusData, currentMonth, currentYear]);

  // Copy Forward "Copy from" options (SCRUM-871): straight from the
  // yearly-document-status response (the rolling 12-month window ending at
  // the current month) - only months that have a care plan (care_plan_id),
  // excluding the current month itself since that's the copy target.
  // Newest first. Year comes from the label ("August - 2025"), same
  // fallback as monthDocumentStatusMap above.
  const copyForwardMonthOptions = useMemo(() => {
    return (documentStatusData?.data || [])
      .map((entry) => {
        const year =
          Number(entry.label?.split(" - ")[1]) ||
          (entry.month > currentMonth ? currentYear - 1 : currentYear);
        return { month: entry.month, year, careplanId: entry.care_plan_id };
      })
      .filter(
        ({ month, year, careplanId }) =>
          careplanId && !(month === currentMonth && year === currentYear)
      )
      .sort((a, b) => b.year - a.year || b.month - a.month)
      .map(({ month, year }) => ({ month, year, label: `${monthNames[month - 1]} ${year}` }));
    // monthNames is a constant list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentStatusData, currentMonth, currentYear]);

  // Top header row (SCRUM-468 Section 1): CCM label, expand-to-fullscreen
  // icon, Create CarePlan dropdown (when no plan exists yet), month/year
  // dropdown, share icon, download icon.
  const headerLeft = (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      <h2 style={{ margin: 0, fontSize: "16px", fontWeight: 600, color: "#0D1139" }}>CCM</h2>
      <Tooltip content={isCcmFullscreen ? "Exit fullscreen" : "Expand to fullscreen"} position="top">
        <button
          type="button"
          onClick={() => setIsCcmFullscreen((prev) => !prev)}
          aria-label={isCcmFullscreen ? "Exit fullscreen" : "Expand to fullscreen"}
          style={circleIconButtonStyle}
        >
          <FontAwesomeIcon icon={isCcmFullscreen ? faCompress : faExpand} style={{ fontSize: "16px", color: "#0D1139" }} />
        </button>
      </Tooltip>

      {/* "View Only" pill + lock icon, per Figma — shown only for the two
          statuses that lock the wizard for editing (finalized/completed),
          not the other read-only reasons (PDF import, patient viewer,
          historical month). Disappears once the plan is editable again. */}
      {isViewOnlyStatus && (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            padding: "4px",
            borderRadius: "20px",
            backgroundColor: "rgba(201, 203, 213, 0.5)",
            color: "#565975",
            fontSize: "14px",
          }}
        >
          <Lock size={14} />
          View Only
        </span>
      )}
    </div>
  );

  const actions = (
    <>
      {/* "Edit Careplan" — per Figma, pairs with the "View Only" pill above.
          Reuses the same status-toggle action as the status pill
          (handleCareplanStatus). All three roles see it while Completed
          (completed -> in_progress). While Finalized, only a Nurse sees it
          (finalized -> in_progress) — Case Manager/Admin can still reopen
          a finalized plan via the status pill itself, they just don't get
          this shortcut button for that specific step. */}
      {(isCareplanCompleted || (isFinalized && isNurseDashboard)) && (
        <Button
          variant="primary"
          onClick={() => handleCareplanStatus(carePlanData?.data?.status)}
          style={{ ...navyButtonStyle, padding: "6px 10px", fontSize: "14px" }}
        >
          Edit Careplan
        </Button>
      )}

      {!isPatient && (
        <div
          style={
            carePlanData ||
              // A new careplan can only be created for the CURRENT month.
              // Past months must already have a careplan (edited via Update),
              // so hide the "Create CarePlan" button for any historical month.
              !(selectedMonth?.month === currentMonth && selectedMonth?.year === currentYear) ||
              isCcmChatStarted ||
              creationDisabled ||
              carePlan_loading
              ? // ? { pointerEvents: "none", opacity: "0.5", cursor: "not-allowed",  }
              { display: "none" }
              : {}
          }
        >
          <Dropdown
            align="right"
            onChange={(value) => handleCareplan(value)}
            trigger={
              <Button variant="ghost">
                <img
                  src="/icons/plus.svg"
                  alt="categoryicon"
                  style={{ width: "14px", height: "14px" }}
                />{" "}
                Create CarePlan
                <FaChevronDown />
              </Button>
            }
          >
            <DropdownItem key="create" value="manual">
              Manual
            </DropdownItem>
            <DropdownItem key="copilot" value="copilot">
              Toca Copilot
            </DropdownItem>
          </Dropdown>
        </div>
      )}

      {/* SCRUM-738: "Prefill from Last Month" button temporarily removed
          from the UI — carryover is blocked on backend work; the handler,
          state, and confirm/loading logic below are left in place to
          reconnect once that's ready. */}

      <Dropdown
        align="left"
        value={`${selectedMonth.month}-${selectedMonth.year}`}
        onChange={handleMonthChange}
        trigger={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              padding: "0 12px",
              height: "35px",
              boxSizing: "border-box",
              fontSize: "14px",
              color: "#0D1139",
              borderRadius: "6px",
              border: "1px solid #C9CBD5",
              width: "170px",
              cursor: "pointer",
              background: "#fff",
            }}
          >
            {monthNames[selectedMonth.month - 1]} - {selectedMonth.year}
            <FaChevronDown />
          </div>
        }
      >
        {last12Months.map(({ month, year }) => (
          <DropdownItem key={`${month}-${year}`} value={`${month}-${year}`}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: "8px",
              }}
            >
              <span>
                {monthNames[month - 1]} - {year}
              </span>
              {monthDocumentStatusMap[`${month}-${year}`] === "pdf" && (
                <img
                  src="/assets/images/svgs/hugeicons_pdf-02.svg"
                  alt="PDF"
                  style={{ width: "16px", height: "16px" }}
                />
              )}
            </div>
          </DropdownItem>
        ))}
      </Dropdown>

      {!isPatient && (
        <Tooltip content="Copy previous month's care plan." position="top">
          <button
            type="button"
            aria-label="Copy previous month's care plan"
            onClick={() => setShowCopyForwardModal(true)}
            style={circleIconButtonStyle}
          >
            <Copy size={16} color="#0D1139" />
          </button>
        </Tooltip>
      )}

      {!isPatient && (
        <div
          style={
            !carePlanData
              ? {
                pointerEvents: "none",
                opacity: "0.5",
                cursor: "not-allowed",
              }
              : {}
          }
        >
          <Button
            variant="ghost"
            aria-label="Share care plan"
            onClick={() => setIsShareingModalOpen(true)}
            style={circleIconButtonStyle}
          >
            <PiShareFat style={{ fontSize: "16px", color: "#0D1139" }} />
          </Button>

          <div style={{ cursor: 'auto' }}>
            <SharingModal
              isOpen={isShareingModalOpen}
              setIsOpen={setIsShareingModalOpen}
              onShare={handleCareplanSharing}
              isSharing={sharing}
              title="Share Care Plan"
              shareButtonText="Share Care Plan"
              showExternalProvider={true}
              showFormatSelector={true}
              availableFormats={["pdf"]}
              defaultFormat="pdf"
              defaultShareType="provider"
              patientId={patient_id}
            />
          </div>
        </div>
      )}

      <div
        style={
          !carePlanData
            ? { pointerEvents: "none", opacity: "0.5", cursor: "not-allowed" }
            : {}
        }
      >
        <Button
          variant="ghost"
          aria-label="Download care plan"
          onClick={() => handleCareplanDownload()}
          disabled={carePlanError}
          style={circleIconButtonStyle}
        >
          <FiDownload style={{ fontSize: "16px", color: "#0D1139" }} />
        </Button>
      </div>
    </>
  );

  // NURSE
  const [showCareplanReviewModal, setShowCareplanReviewModal] = useState(false);

  // Health Summary physiological field label with the "previous 1 year"
  // history icon on its right (opens HealthSummaryHistoryModal).
  const renderHealthHistoryLabel = (label, metric) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px" }}>
      <label style={getFieldLabelStyle(isReadOnly)}>{label}</label>
      <Tooltip content={`Previous 1 year ${label}`} position="top">
        <button
          type="button"
          aria-label={`View previous 1 year ${label}`}
          onClick={() => setHealthHistoryField({ metric, label })}
          style={{ display: "flex", padding: 0, border: "none", background: "none", cursor: "pointer" }}
        >
          <img src={calendarHistoryIcon} alt="" width={17} height={17} />
        </button>
      </Tooltip>
    </div>
  );

  // One Health Summary physiological field: history label, auto-grow input
  // with the unit shown on the right, blood pressure check once edited.
  const renderPhysiologicalField = (label, field) => {
    const rule = PHYSIOLOGICAL_FIELD_RULES[field];
    const value = formData?.physiological_data?.[field] || "";
    const error = getPhysiologicalError(field, value);
    const showError = physiologicalEdits[field] === "blurred" && !!error;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "2px", flex: "1 1 100px" }}>
        {renderHealthHistoryLabel(label, field)}
        <div style={{ position: "relative" }}>
          <textarea
            ref={(el) => (textareaRefs.current[field] = el)}
            name={field}
            readOnly={isReadOnly}
            placeholder={isReadOnly ? "" : rule.placeholder}
            value={value}
            onInput={handleTextAreaInput}
            onChange={(e) => {
              handleNestedInputChange("physiological_data", field, sanitizePhysiologicalValue(e.target.value));
              setPhysiologicalEdits((prev) => ({ ...prev, [field]: prev[field] || "editing" }));
            }}
            onBlur={() =>
              setPhysiologicalEdits((prev) => (prev[field] ? { ...prev, [field]: "blurred" } : prev))
            }
            aria-invalid={showError}
            style={{
              ...getAutoGrowFieldStyle(isReadOnly),
              minHeight: "36px",
              display: "block",
              ...(rule.unit ? { paddingRight: "52px" } : {}),
              // The shared textarea margin-bottom would push the error away.
              ...(showError ? { borderColor: "#ef4444", marginBottom: 0 } : {}),
            }}
          />
          {rule.unit && (
            <span
              style={{
                position: "absolute",
                right: "10px",
                top: "9px",
                fontSize: "12px",
                color: "#565975",
                pointerEvents: "none",
              }}
            >
              {rule.unit}
            </span>
          )}
        </div>
        {showError && (
          <span style={{ color: "#ef4444", fontSize: "12px", lineHeight: "16px", marginTop: "4px" }}>
            {error}
          </span>
        )}
      </div>
    );
  };

  const content = (
    <>
      {/* carePlanData is intentionally checked here too — carePlan_loading
          (React Query's isFetching) is also true on the background refetch
          that follows every save, not just the very first load. Without the
          !carePlanData check, that refetch replaced the whole wizard with
          this bare spinner on every save — collapsing the panel's height
          and forcing a scroll jump — even though the previous data (and the
          full wizard) was still perfectly valid to keep showing. */}
      {(carePlan_loading && !carePlanData) || aiCareplanloading ? (
        <p style={{ marginTop: "30px" }}>
          <Spinner />
          {aiCareplanloading && <p>Toca Copilot is generating Careplan</p>}
        </p>
      ) : (
        <div className={careplan_modal.careplan_wrapper}>
          {careplanId != undefined ? (
            <>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  // alignItems: "center"
                  // padding: "0px 10px",
                }}
              >
                {/* Section 2 (SCRUM-468): 6-step wizard progress indicator. */}
                <div
                  role="tablist"
                  aria-label="Care plan wizard steps"
                  style={{
                    position: "relative",
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    backgroundColor: "#F5F5F5",
                    borderBottom: "1px solid #C9CBD5",
                    // borderRadius: "6px",
                    padding: "10px 110px",
                    boxSizing: "border-box",
                  }}
                >
                  {/* Single continuous line behind every circle (per Figma),
                      rather than a segment squeezed into the flex gap
                      between each pair of steps — that approach had no room
                      left to render once the step labels ate the available
                      width in a narrow container. The circles (opaque
                      backgrounds, painted after this in DOM order) sit on
                      top and hide the line where they overlap it. */}
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      top: "19px",
                      left: "140px",
                      right: "160px",
                      height: "2px",
                      backgroundImage:
                        "repeating-linear-gradient(to right, #DADBE1 0px, #DADBE1 6px, transparent 6px, transparent 12px)",
                    }}
                  />

                  {WIZARD_STEPS.map(({ step, labelLines }, index) => {
                    // A step is "done" (green) if it genuinely has data saved
                    // — not merely reachable — so a brand-new plan shows grey
                    // until each step is actually filled in and saved, while
                    // a pre-existing/complete plan (which unlocks every step
                    // immediately) correctly shows every filled step green.
                    const isFilled = isWizardStepFilled(step);
                    const isActive = step === currentStep;
                    const circleColor = isActive ? "#0D1139" : isFilled ? "#2FA36E" : "#C9CBD5";

                    return (
                      <React.Fragment key={step}>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={isActive}
                          onClick={() => handleWizardStepClick(step)}
                          style={{
                            position: "relative",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "4px",
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                            minWidth: "80px",
                          }}
                        >
                          <span
                            style={{
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "20px",
                              height: "20px",
                            }}
                          >
                            {/* Square backdrop, larger than the round circle,
                                so the dashed connector line is fully masked
                                behind the number — the round shape alone
                                leaves slivers of the line visible right at
                                its edge. */}
                            <span
                              aria-hidden="true"
                              style={{
                                position: "absolute",
                                inset: "-4px",
                                backgroundColor: "#F5F5F5",
                              }}
                            />
                            <span
                              style={{
                                position: "relative",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "20px",
                                height: "20px",
                                borderRadius: "50%",
                                backgroundColor: circleColor,
                                color: isActive || isFilled ? "#fff" : "#565975",
                                fontSize: "12px",
                                fontWeight: 400,
                              }}
                            >
                              {step}
                            </span>
                          </span>
                          <span
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              fontSize: "12px",
                              textAlign: "center",
                              color: isActive ? "#0D1139" : "#565975",
                              fontWeight: 400,
                            }}
                          >
                            {labelLines.map((line) => (
                              <span key={line}>{line}</span>
                            ))}
                          </span>
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>

                {/* Section 3 (SCRUM-468): sub-header / metadata row — same
                    fields/behavior as before, unified into one row instead of
                    a duplicated isNurseDashboard/default block. The status
                    pill's click handler is one shared cycle for all three
                    roles (Admin/Care Manager/Nurse) — see its onClick below. */}
                <div
                  className={careplan_modal.metadata_section}
                  style={{ margin: "10px 0px 0px", borderBottom: "1px solid #C9CBD5", display: "flex", alignItems: "center", flexWrap: "wrap", gap: "15px", padding: "0 20px 10px" }}
                >
                  <div className={careplan_modal.metadata_item}>
                    <span className={careplan_modal.metadata_label}>
                      Created by
                    </span>
                    <span
                      className={careplan_modal.metadata_value}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      {carePlanData?.data?.created_by_name
                        ? carePlanData?.data?.created_by_name
                        : "--"}
                      {/* <Tooltip
                        content={`On ${carePlanData?.data?.created_at
                          ? formatDate(carePlanData?.data?.created_at)
                          : "--"
                          }`}
                        position="top"
                      >
                        <span
                          style={{
                            cursor: "pointer",
                            display: "flex",
                            height: "15px",
                            width: "15px",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#867d7d",
                            borderRadius: "50%",
                            color: "white",
                            fontSize: "10px",
                          }}
                        >
                          ?
                        </span>
                      </Tooltip> */}
                    </span>
                  </div>

                  <span style={{ width: "1px", height: "40px", backgroundColor: "#C9CBD5" }} />

                  <div className={careplan_modal.metadata_item}>
                    <span className={careplan_modal.metadata_label}>
                      Complex
                    </span>
                    <span className={careplan_modal.metadata_value}>
                      <label className={careplan_modal.switch}>
                        <input
                          type="checkbox"
                          checked={formData?.complex}
                          readOnly={isReadOnly}
                          onChange={
                            isReadOnly
                              ? undefined
                              : (e) => {
                                setFormData((prev) => ({
                                  ...prev,
                                  complex: e.target.checked,
                                }));
                              }
                          }
                        />
                        <span className={careplan_modal.slider}></span>
                      </label>
                      <span>{formData?.complex ? "Yes" : "No"}</span>
                    </span>
                  </div>

                  <span style={{ width: "1px", height: "40px", backgroundColor: "#C9CBD5" }} />

                  <div className={careplan_modal.metadata_item}>
                    <span className={careplan_modal.metadata_label}>
                      Status
                    </span>
                    <span
                      className={`${careplan_modal.metadata_value} ${careplan_modal.status}`}
                      style={{
                        cursor: "pointer",
                        width: "fit-content",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "2px 6px",
                        backgroundColor: isFinalized ? "#E8F8F3" : "rgba(72, 166, 196, 0.3)",
                        color: isFinalized ? "#10B981" : "#0D1139",
                        fontSize: "14px",
                        borderRadius: "20px",
                      }}
                      onClick={() => {
                        if (!isHistoricalOrCurrentMonth(selectedMonth, currentMonth, currentYear) || isPatient)
                          return;

                        // One cycle for all three roles (Admin/Care
                        // Manager/Nurse) — Create, Edit, Mark Completed,
                        // and Finalize are the same permission set for
                        // everyone. in_progress -> completed still opens
                        // the Assign Nurse modal, same as
                        // handleCareplanStatus's own in_progress case —
                        // except for a nurse: Assign Nurse is a Case
                        // Manager/Admin step, so a nurse re-completing a
                        // plan herself isn't prompted for it. finalized ->
                        // in_progress is intentionally reversible now —
                        // finalizing is no longer a permanent lock.
                        const currentStatus = carePlanData?.data?.status;
                        if (currentStatus === "in_progress") {
                          mutation.mutate(
                            { patient: patient_id, status: "completed" },
                            {
                              onSuccess: () => {
                                if (!isNurseDashboard) setShowAssignNurseModal(true);
                              },
                            }
                          );
                        } else if (currentStatus === "completed") {
                          mutation.mutate({ patient: patient_id, status: "finalized" });
                        } else if (currentStatus === "finalized") {
                          mutation.mutate({ patient: patient_id, status: "in_progress" });
                        }
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          height: "6px",
                          width: "6px",
                          backgroundColor: isFinalized ? "#10B981" : "#0D1139",
                          borderRadius: "50%",
                        }}
                      ></span>
                      {mutation.isLoading ? (
                        <button className="status_pill status_loading">
                          <div className="spinner"></div>
                        </button>
                      ) : (
                        statusMapping[carePlanData?.data?.status]
                      )}
                    </span>
                  </div>

                  <span style={{ width: "1px", height: "40px", backgroundColor: "#C9CBD5" }} />

                  <span
                    style={{
                      cursor: "pointer",
                      color: "#48A6C4",
                      textDecoration: "underline",
                      fontSize: "14px",
                    }}
                    onClick={() => !isPatient && setShowCareplanReviewModal(true)}
                  >
                    {isNurseDashboard ? "Add Review" : "View Reviews"}
                  </span>

                  {carePlanData?.data?.careplan_document_url && (
                    <Button
                      variant="ghost"
                      onClick={handleViewPdf}
                      aria-label="View care plan PDF document"
                      style={{ marginLeft: "auto", backgroundColor: "#48A6C4", borderColor: "#48A6C4", color: "#fff", borderRadius: "6px" }}
                    >
                      View Uploaded Careplan{" "}
                      <Tooltip
                        content="This care plan was finalized as a PDF document. Editing is disabled; use View PDF to open the document."
                        position="top"
                      >
                        <span
                          style={{
                            cursor: "pointer",
                            display: "flex",
                            height: "15px",
                            width: "15px",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#fff",
                            borderRadius: "50%",
                            color: "#867d7d",
                            fontSize: "10px",
                          }}
                        >
                          ?
                        </span>
                      </Tooltip>
                    </Button>
                  )}
                </div>

                <div
                  className={`${careplan_modal.careplan} ${careplan_modal.step_fade_in}`}
                  style={currentStep !== 1 ? { display: "none" } : undefined}
                >
                  <div className={careplan_modal.careplan_body}>
                    {!formData?.chronic_conditions?.length ? (
                      // SCRUM-469 empty state.
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "12px",
                          padding: "50px 0",
                        }}
                      >
                        <p style={{ margin: 0, color: "#565975", fontSize: "14px" }}>
                          No chronic condition added
                        </p>
                        {!isReadOnly && (
                          <Button variant="ghost" onClick={handleAddCondition} style={navyButtonStyle}>
                            + Add Chronic Condition
                          </Button>
                        )}
                      </div>
                    ) : (
                      <>
                        {/* Condition tabs row — grey band per Figma. */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            backgroundColor: "#F5F5F5",
                            borderBottom: "1px solid #C9CBD5",
                            padding: "10px 20px",
                            boxSizing: "border-box",
                          }}
                        >
                          {canScrollTabsLeft && (
                            <button
                              type="button"
                              onClick={() => scrollTabsBy(-160)}
                              aria-label="Scroll conditions left"
                              style={tabsScrollButtonStyle}
                            >
                              <ChevronDown size={14} style={{ transform: "rotate(90deg)" }} />
                            </button>
                          )}

                          <div
                            ref={tabsScrollRef}
                            onScroll={updateTabsScrollState}
                            style={{ display: "flex", overflowX: "auto", flex: 1, scrollbarWidth: "none", border: "1px solid #C9CBD5" }}
                          >
                            {/* Included conditions sort first (stable — ties
                                keep their original relative order), per
                                design feedback; a plain copy so add/edit/
                                delete (all key-based, not index-based) stay
                                unaffected. */}
                            {[...formData.chronic_conditions]
                              .sort((a, b) => Number(Boolean(b.for_billing_icd)) - Number(Boolean(a.for_billing_icd)))
                              .map((condition, index, sortedConditions) => {
                              const conditionKey = condition.id || condition.tempId;
                              const isActive = conditionKey === activeConditionKeyResolved;
                              const isIncluded = Boolean(condition.for_billing_icd);

                              return (
                                <button
                                  key={conditionKey}
                                  type="button"
                                  onClick={() => setActiveConditionKey(conditionKey)}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    padding: "10px 16px",
                                    border: "none",
                                    borderRight: index !== sortedConditions.length - 1 ? "1px solid #C9CBD5" : "none",
                                    borderRadius: isActive ? "6px" : "0",
                                    cursor: "pointer",
                                    backgroundColor: isActive ? "#0D1139" : isIncluded ? "#EDF7FA" : "transparent",
                                    color: isActive ? "#fff" : isIncluded ? "#48A6C4" : "#565975",
                                    fontWeight: isActive ? 600 : 400,
                                    fontSize: "14px",
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                  }}
                                >
                                  {isIncluded && (
                                    <span
                                      style={{
                                        display: "inline-block",
                                        width: "8px",
                                        height: "8px",
                                        borderRadius: "50%",
                                        backgroundColor: "#48A6C4",
                                      }}
                                    />
                                  )}
                                  {getConditionChipLabel(condition, index)}
                                </button>
                              );
                            })}
                          </div>

                          {canScrollTabsRight && (
                            <button
                              type="button"
                              onClick={() => scrollTabsBy(160)}
                              aria-label="Scroll conditions right"
                              style={tabsScrollButtonStyle}
                            >
                              <ChevronDown size={14} style={{ transform: "rotate(-90deg)" }} />
                            </button>
                          )}

                          {!isReadOnly && (
                            <Button
                              variant="ghost"
                              onClick={handleAddCondition}
                              style={navyButtonStyle}
                            >
                              + Add Chronic Condition
                            </Button>
                          )}
                        </div>

                        {/* Active condition's detail form */}
                        {activeCondition && (
                          <div style={{ display: "flex", flexDirection: "column", gap: "11px", padding: "0px 20px" }}>
                            <div
                              style={{
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",

                              }}
                            >
                              <h3 style={{ flex: 1, margin: 0, color: "#0D1139", fontSize: "16px", fontWeight: 600 }}>
                                {`${activeCondition.name || "Name"} (${activeCondition.icd_code || "ICD Code"})`}
                              </h3>

                              <label
                                style={{
                                  cursor: isReadOnly ? "default" : "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  padding: "4px 12px",
                                  borderRadius: "20px",
                                  border: `1px solid ${activeCondition?.for_billing_icd ? "#48A6C4" : "#C9CBD5"}`,
                                  color: activeCondition?.for_billing_icd ? "#48A6C4" : "#565975",
                                  backgroundColor: activeCondition?.for_billing_icd ? "#EDF7FA" : "transparent",
                                  fontSize: "14px",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={activeCondition?.for_billing_icd}
                                  readOnly={isReadOnly}
                                  style={{ display: "none" }}
                                  onChange={
                                    isReadOnly
                                      ? undefined
                                      : () => {
                                        // Trying to include a THIRD condition
                                        // opens the picker popup (per Figma)
                                        // instead of just rejecting — un-
                                        // checking, or checking while under
                                        // the limit, stays a direct toggle.
                                        const billedCount = formData?.chronic_conditions?.filter(
                                          (cond) => cond.for_billing_icd
                                        ).length;
                                        if (!activeCondition?.for_billing_icd && billedCount >= 2) {
                                          openIncludeConditionModal();
                                        } else {
                                          handleBillingCheckBox(activeConditionKeyResolved);
                                        }
                                      }
                                  }
                                />
                                {activeCondition?.for_billing_icd && (
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: "8px",
                                      height: "8px",
                                      borderRadius: "50%",
                                      backgroundColor: "#48A6C4",
                                    }}
                                  />
                                )}
                                {activeCondition?.for_billing_icd ? "Condition Included" : "Include Condition"}
                              </label>

                              {!isReadOnly && (
                                <div
                                  role="button"
                                  tabIndex={0}
                                  onClick={() =>
                                    handleDeleteCondition(
                                      activeCondition,
                                      formData.chronic_conditions.findIndex(
                                        (c) => (c.id ?? c.tempId) === activeConditionKeyResolved
                                      )
                                    )
                                  }
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "26px",
                                    height: "26px",
                                    border: "1px solid #EC221F",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    flexShrink: 0,
                                  }}
                                >
                                  <FiTrash2
                                    className="icon-default-size"
                                    style={{ color: "#EC221F" }}
                                  />
                                </div>
                              )}
                            </div>

                            <div className={careplan_modal.input_group}>
                              <div className={careplan_modal.input_field}>
                                <label style={getFieldLabelStyle(isReadOnly)}>Chronic condition</label>
                                {isReadOnly ? (
                                  <input
                                    type="text"
                                    readOnly
                                    value={activeCondition.name || ""}
                                    style={getFieldBoxStyle(isReadOnly)}
                                  />
                                ) : (
                                  <div className={conditionSelector.dropdown} style={{ width: "100%", margin: "0px" }}>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setOpenTempId((prev) =>
                                          prev === activeConditionKeyResolved ? null : activeConditionKeyResolved
                                        )
                                      }
                                      style={{
                                        ...getFieldBoxStyle(false),
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        cursor: "pointer",
                                        color: activeCondition.name ? "#0D1139" : "#565975",
                                      }}
                                    >
                                      <span>{activeCondition.name || "Select Chronic Condition"}</span>
                                      <ChevronDown size={16} className={conditionSelector.icon} />
                                    </button>
                                    {openTempId === activeConditionKeyResolved && (
                                      <div ref={activeDropdownRef} className={conditionSelector.dropdownContent}>
                                        <div className={conditionSelector.searchContainer}>
                                          <input
                                            type="text"
                                            className={conditionSelector.searchInput}
                                            placeholder="Search condition..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                          />
                                        </div>
                                        <div className={conditionSelector.medicationList}>
                                          {filteredConditions?.length === 0 ? (
                                            <div className={conditionSelector.emptyMessage}>
                                              No condition found.
                                            </div>
                                          ) : (
                                            filteredConditions?.map((filteredCondition) => (
                                              <button
                                                key={filteredCondition?.id}
                                                className={conditionSelector.medicationItem}
                                                type="button"
                                                onClick={() =>
                                                  handlePopulateCondition(
                                                    filteredCondition,
                                                    activeConditionKeyResolved
                                                  )
                                                }
                                              >
                                                <span>{filteredCondition?.name}</span>
                                              </button>
                                            ))
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                              <div className={careplan_modal.input_field}>
                                <label style={getFieldLabelStyle(isReadOnly)}>ICD Code</label>
                                <input
                                  type="text"
                                  placeholder="ICD Code"
                                  readOnly={isReadOnly}
                                  value={activeCondition.icd_code || ""}
                                  onChange={(e) =>
                                    updateConditionField(
                                      activeConditionKeyResolved,
                                      "icd_code",
                                      e.target.value
                                    )
                                  }
                                  style={getFieldBoxStyle(isReadOnly)}
                                />
                              </div>
                            </div>

                            <div className={careplan_modal.input_group}>
                              <div className={careplan_modal.input_field}>
                                <LimitedFreeTextSection
                                  maxLength={FREE_TEXT_MAX_LENGTH}
                                  label="Prognosis"
                                  placeholder="Prognosis"
                                  readOnly={isReadOnly}
                                  value={activeCondition.prognosis || ""}
                                  onChange={(val) =>
                                    updateConditionField(activeConditionKeyResolved, "prognosis", val)
                                  }
                                  headerExtra={
                                    // SCRUM-749: reference for the matching
                                    // Chronic Condition Template's prognosis
                                    // options, only shown when a matching
                                    // template has any. onInsert appends the
                                    // chosen option as a new line onto
                                    // whatever is already in Prognosis - it
                                    // never replaces or clears existing text.
                                    !isReadOnly && prognosisOptionsForActiveCondition.length > 0 ? (
                                      <PrognosisTemplateReference
                                        options={prognosisOptionsForActiveCondition}
                                        onInsert={(text) => {
                                          const current = activeCondition?.prognosis || "";
                                          updateConditionField(
                                            activeConditionKeyResolved,
                                            "prognosis",
                                            current ? `${current}\n${text}` : text
                                          );
                                        }}
                                      />
                                    ) : null
                                  }
                                />
                              </div>

                              <div className={careplan_modal.input_field}>
                                {wasCreatedOnOrAfterSymptomsCutoff(carePlanData?.data?.created_at) ? (
                                  <SymptomTriStateList
                                    label="Symptoms"
                                    placeholder="Symptoms"
                                    readOnly={isReadOnly}
                                    value={activeCondition.symptoms || ""}
                                    onChange={(val) =>
                                      updateConditionField(activeConditionKeyResolved, "symptoms", val)
                                    }
                                  />
                                ) : (
                                  <LimitedFreeTextSection
                                    maxLength={FREE_TEXT_MAX_LENGTH}
                                    label="Symptoms"
                                    placeholder="Symptoms"
                                    readOnly={isReadOnly}
                                    minHeight={100}
                                    value={activeCondition.symptoms || ""}
                                    onChange={(val) =>
                                      updateConditionField(activeConditionKeyResolved, "symptoms", val)
                                    }
                                  />
                                )}
                              </div>
                            </div>

                            <div className={careplan_modal.input_group}>
                              <div className={careplan_modal.input_field}>
                                <LimitedFreeTextSection
                                  maxLength={FREE_TEXT_MAX_LENGTH}
                                  label="Treatment Goals"
                                  placeholder="Treatment Goals"
                                  readOnly={isReadOnly}
                                  minHeight={100}
                                  value={activeCondition.treatment_goals || ""}
                                  onChange={(val) =>
                                    updateConditionField(activeConditionKeyResolved, "treatment_goals", val)
                                  }
                                />
                              </div>

                              <div className={careplan_modal.input_field}>
                                <LimitedFreeTextSection
                                  maxLength={FREE_TEXT_MAX_LENGTH}
                                  label="Planned Intervention"
                                  placeholder="Planned Interventions"
                                  readOnly={isReadOnly}
                                  minHeight={100}
                                  value={activeCondition.planned_interventions || ""}
                                  onChange={(val) =>
                                    updateConditionField(activeConditionKeyResolved, "planned_interventions", val)
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div
                  className={`${careplan_modal.careplan} ${careplan_modal.step_fade_in}`}
                  style={{
                    padding: "0px 20px",
                    ...(currentStep !== 2 ? { display: "none" } : {}),
                  }}
                >
                  <h3 style={sectionHeadingStyle}>Medication Reconciliation</h3>

                  <div className={careplan_modal.careplan_body}>
                    <div className={careplan_modal.input_group}>
                      <div className={careplan_modal.input_field}>
                        <LimitedFreeTextSection
                          maxLength={FREE_TEXT_MAX_LENGTH}
                          label="Reported Side Effect"
                          placeholder=""
                          readOnly={isReadOnly}
                          value={formData?.reported_side_effect || ""}
                          onChange={(val) => {
                            setFormData((prev) => ({
                              ...prev,
                              reported_side_effect: val,
                            }));
                          }}
                        />
                      </div>

                      <div className={careplan_modal.input_field}>
                        <LimitedFreeTextSection
                          maxLength={FREE_TEXT_MAX_LENGTH}
                          label="Allergies"
                          placeholder=""
                          readOnly={isReadOnly}
                          value={formData?.allergies || ""}
                          onChange={(val) => {
                            setFormData((prev) => ({
                              ...prev,
                              allergies: val,
                            }));
                          }}
                        />
                      </div>
                    </div>

                    <div className={careplan_modal.input_group}>
                      <div className={careplan_modal.input_field}>
                        <LimitedFreeTextSection
                          maxLength={FREE_TEXT_MAX_LENGTH}
                          label="Medications"
                          placeholder=""
                          readOnly={isReadOnly}
                          minHeight={100}
                          value={formData?.medications?.[0]?.name || ""}
                          onChange={(val) => {
                            setFormData((prev) => {
                              const existing = prev.medications?.[0] || {};
                              return {
                                ...prev,
                                medications: [
                                  {
                                    ...(existing.id ? { id: existing.id } : {}),
                                    name: val,
                                  },
                                ],
                              };
                            });
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className={`${careplan_modal.careplan} ${careplan_modal.step_fade_in}`}
                  style={{
                    padding: "0px 20px",
                    ...(currentStep !== 3 ? { display: "none" } : {}),
                  }}
                >
                  <h3 style={sectionHeadingStyle}>Health Summary</h3>

                  <div className={careplan_modal.careplan_body}>
                    <h4 style={subsectionHeadingStyle}>Physiological Data Averages:</h4>

                    <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
                      {renderPhysiologicalField("Weight", "weight")}

                      {renderPhysiologicalField("Blood Glucose", "blood_glucose")}

                      {renderPhysiologicalField("BMI", "bmi")}

                      {renderPhysiologicalField("Blood Pressure", "blood_pressure")}

                      {renderPhysiologicalField("Height", "height")}
                    </div>

                    <h4 style={subsectionHeadingStyle}>Behavioral and Nutrition Averages:</h4>

                    <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
                      {/* Bug fix: swapped from plain <textarea>s to
                          FreeTextSection (same as Careplan Notes above) so
                          Sleep/Diet-Water/Exercise get the same AI-delta
                          "**bold**(added/updated MM/DD/YYYY)" rendering as
                          every other AI-populated field - these three were
                          still showing raw, unstyled asterisks. */}
                      <div style={{ display: "flex", flexDirection: "column", gap: "2px", flex: "1 1 200px" }}>
                        <LimitedFreeTextSection
                          maxLength={FREE_TEXT_MAX_LENGTH}
                          label="Sleep"
                          placeholder=""
                          readOnly={isReadOnly}
                          minHeight={36}
                          showClear={false}
                          value={formData?.behavioral_data?.sleep || ""}
                          onChange={(val) => handleNestedInputChange("behavioral_data", "sleep", val)}
                        />
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: "2px", flex: "1 1 200px" }}>
                        <LimitedFreeTextSection
                          maxLength={FREE_TEXT_MAX_LENGTH}
                          label="Diet/Water"
                          placeholder=""
                          readOnly={isReadOnly}
                          minHeight={36}
                          showClear={false}
                          value={formData?.behavioral_data?.water_diet || ""}
                          onChange={(val) => handleNestedInputChange("behavioral_data", "water_diet", val)}
                        />
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: "2px", flex: "1 1 200px" }}>
                        <LimitedFreeTextSection
                          maxLength={FREE_TEXT_MAX_LENGTH}
                          label="Exercise"
                          placeholder=""
                          readOnly={isReadOnly}
                          minHeight={36}
                          showClear={false}
                          value={formData?.behavioral_data?.exercise || ""}
                          onChange={(val) => handleNestedInputChange("behavioral_data", "exercise", val)}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className={`${careplan_modal.careplan} ${careplan_modal.step_fade_in}`}
                  style={{
                    padding: "0px 20px",
                    ...(currentStep !== 4 ? { display: "none" } : {}),
                  }}
                >
                  <h3 style={sectionHeadingStyle}>Problem List &amp; Education</h3>

                  <div className={careplan_modal.careplan_body}>
                    <div className={careplan_modal.input_group}>
                      <div className={careplan_modal.input_field}>
                        <LimitedFreeTextSection
                          maxLength={FREE_TEXT_MAX_LENGTH}
                          label="Past Medical History"
                          placeholder=""
                          readOnly={isReadOnly}
                          minHeight={100}
                          value={formData?.pmhx || ""}
                          onChange={(val) => {
                            setFormData((prev) => ({
                              ...prev,
                              pmhx: val,
                            }));
                          }}
                        />
                      </div>

                      <div className={careplan_modal.input_field}>
                        <LimitedFreeTextSection
                          maxLength={FREE_TEXT_MAX_LENGTH}
                          label="Potential Barriers"
                          placeholder=""
                          readOnly={isReadOnly}
                          minHeight={100}
                          value={formData?.potential_barriers || ""}
                          onChange={(val) => {
                            setFormData((prev) => ({
                              ...prev,
                              potential_barriers: val,
                            }));
                          }}
                        />
                      </div>
                    </div>

                    <div className={careplan_modal.input_group}>
                      <div className={careplan_modal.input_field}>
                        <LimitedFreeTextSection
                          maxLength={FREE_TEXT_MAX_LENGTH}
                          label="Recommendations"
                          placeholder=""
                          readOnly={isReadOnly}
                          minHeight={100}
                          value={formData?.recommendations || ""}
                          onChange={(val) => {
                            setFormData((prev) => ({
                              ...prev,
                              recommendations: val,
                            }));
                          }}
                        />
                      </div>

                      <div className={careplan_modal.input_field}>
                        <LimitedFreeTextSection
                          maxLength={FREE_TEXT_MAX_LENGTH}
                          label="Recent Hospitalization"
                          placeholder=""
                          readOnly={isReadOnly}
                          minHeight={100}
                          value={formData?.recent_hospitalization || ""}
                          onChange={(val) => {
                            setFormData((prev) => ({
                              ...prev,
                              recent_hospitalization: val,
                            }));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className={`${careplan_modal.careplan} ${careplan_modal.step_fade_in}`}
                  style={{
                    padding: "0px 20px",
                    ...(currentStep !== 5 ? { display: "none" } : {}),
                  }}
                >
                  <h3 style={sectionHeadingStyle}>Functional Assessment</h3>

                  <div className={careplan_modal.careplan_body}>
                    <div className={careplan_modal.input_group}>
                      <div className={careplan_modal.input_field}>
                        <LimitedFreeTextSection
                          maxLength={FREE_TEXT_MAX_LENGTH}
                          label="Activities of Daily Living"
                          placeholder=""
                          readOnly={isReadOnly}
                          minHeight={100}
                          value={formData?.activities_of_daily_living || ""}
                          onChange={(val) => {
                            setFormData((prev) => ({
                              ...prev,
                              activities_of_daily_living: val,
                            }));
                          }}
                        />
                      </div>

                      <div className={careplan_modal.input_field}>
                        <LimitedFreeTextSection
                          maxLength={FREE_TEXT_MAX_LENGTH}
                          label="Caregiver Assessment"
                          placeholder=""
                          readOnly={isReadOnly}
                          minHeight={100}
                          value={formData?.caregiver_assessment || ""}
                          onChange={(val) => {
                            setFormData((prev) => ({
                              ...prev,
                              caregiver_assessment: val,
                            }));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className={`${careplan_modal.careplan} ${careplan_modal.step_fade_in}`}
                  style={{
                    padding: "0px 20px",
                    ...(currentStep !== 6 ? { display: "none" } : {}),
                  }}
                >
                  <h3 style={sectionHeadingStyle}>Careplan Notes</h3>

                  <div className={careplan_modal.careplan_body}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "2px", width: "100%" }}>
                      {/* SCRUM-788: swapped from a plain <textarea> to
                          FreeTextSection so Careplan Notes gets the same
                          "**bold**(added/updated MM/DD/YYYY)" AI-delta
                          rendering as Prognosis/Treatment Goals/Planned
                          Intervention - it was the one narrative field the
                          ticket explicitly names that this page still
                          rendered as raw, unstyled text. showClear=false
                          keeps the exact same controls as before (no clear
                          button previously existed on this field). */}
                      <LimitedFreeTextSection
                        maxLength={CAREPLAN_NOTES_MAX_LENGTH}
                        label="Notes"
                        placeholder="Careplan notes..."
                        readOnly={isReadOnly}
                        minHeight={229}
                        showClear={false}
                        value={formData?.note || ""}
                        onChange={(val) =>
                          setFormData((prev) => ({
                            ...prev,
                            note: val,
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Section 5 (SCRUM-468): Back / Save & Continue / Update &
                    Continue / Confirm Careplan navigation — hidden whenever
                    the plan is read-only (finalized, completed, PDF-imported,
                    patient viewer, or a future/non-editable month). Finalized
                    and completed plans (isViewOnlyStatus) get the Back/"View
                    Next" row below instead; the other read-only reasons
                    navigate via the stepper circles above
                    only, since they have nothing to save either way. */}
                {!isReadOnly && (
                  <>
                    <div style={{ borderTop: "1px solid #C9CBD5" }} />
                    {/* Own row above the buttons (not beside them) so a long
                        list of over-limit fields can wrap freely without
                        squeezing Back / Save & Continue. */}
                    {hasCharLimitErrors && (
                      <div
                        style={{
                          color: "#ef4444",
                          fontSize: "12px",
                          lineHeight: "16px",
                          padding: "8px 20px 0",
                        }}
                      >
                        Character limit exceeded in {charLimitErrorFields.length}{" "}
                        {charLimitErrorFields.length === 1 ? "field" : "fields"}: {charLimitErrorFields.join(", ")}
                      </div>
                    )}
                    {hasPhysiologicalErrors && (
                      <div
                        style={{
                          color: "#ef4444",
                          fontSize: "12px",
                          lineHeight: "16px",
                          padding: "8px 20px 0",
                        }}
                      >
                        Health Summary: in{" "}
                        {physiologicalErrorFields.map((field) => PHYSIOLOGICAL_FIELD_LABELS[field]).join(", ")}, the
                        systolic (first number) must be greater than diastolic
                      </div>
                    )}
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: "10px",
                        paddingInline:'20px',
                        paddingBottom:'10px',
                      }}
                    >
                      <Button
                        variant="ghost"
                        onClick={handleWizardBack}
                        disabled={currentStep === 1}
                        style={{
                          border: "1px solid #C9CBD5",
                          borderRadius: "6px",
                          color: "#0D1139",
                          backgroundColor: "#F8FAFD",
                          visibility: currentStep === 1 ? "hidden" : "visible",
                        }}
                      >
                        Back
                      </Button>

                      <Button
                        variant="primary"
                        onClick={handleWizardContinue}
                        disabled={isCareplanLoading || carePlan_loading || hasCharLimitErrors || hasPhysiologicalErrors}
                        style={{ ...navyButtonStyle, padding: "10px 24px", fontSize: "14px" }}
                      >
                        {isCareplanLoading
                          ? "Saving..."
                          : currentStep === WIZARD_STEPS.length
                            ? "Confirm Careplan"
                            : careplanId
                              ? "Update & Continue"
                              : "Save & Continue"}
                      </Button>
                    </div>
                  </>
                )}

                {/* Completed plans are read-only but still step through their
                    content (per Figma's "View Next") rather than losing all
                    navigation — unlike the other read-only reasons above,
                    this one is reversible via the status pill. */}
                {isViewOnlyStatus && (
                  <>
                    <div style={{ borderTop: "1px solid #C9CBD5" }} />
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: "10px",
                        paddingInline: '20px',
                        paddingBottom: '10px',
                      }}
                    >
                      <Button
                        variant="ghost"
                        onClick={handleWizardBack}
                        disabled={currentStep === 1}
                        style={{
                          border: "1px solid #C9CBD5",
                          borderRadius: "6px",
                          color: "#0D1139",
                          backgroundColor: "#F8FAFD",
                          visibility: currentStep === 1 ? "hidden" : "visible",
                        }}
                      >
                        Back
                      </Button>

                      <Button
                        variant="primary"
                        onClick={() => setCurrentStep((step) => Math.min(WIZARD_STEPS.length, step + 1))}
                        style={{
                          ...navyButtonStyle,
                          padding: "10px 24px",
                          fontSize: "14px",
                          visibility: currentStep === WIZARD_STEPS.length ? "hidden" : "visible",
                        }}
                      >
                        View Next
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            // Figma's reference card is ~707px tall including the header;
            // the header renders unconditionally above this, so match the
            // remaining height here instead of collapsing to one line.
            <div
              style={{
                minHeight: "440px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <p style={{ color: "#565975", fontSize: "14px" }}>
                No Care Plan found for this month
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );

  // No CollapsableSection wrapper here anymore: that component's bodyHeight
  // prop is what forced the old fixed-550px internal scrollbar. The wizard
  // has its own custom header (CCM label + expand icon, per SCRUM-468) and
  // renders at its natural content height — the page/window scrolls, this
  // panel never does. isCcmFullscreen (toggled from headerLeft) makes this
  // panel itself the top-level scroll container instead, for the same reason
  // (no nested scroll even in fullscreen).
  return (
    <>
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "10px",
          // padding: "16px 20px"
          overflowX: "hidden",
          ...(isCcmFullscreen
            ? {
              position: "fixed",
              inset: 0,
              zIndex: 1000,
              padding: "50px",
            }
            : {}),
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px",
            padding: " 10px 20px",
            borderBottom: "1px solid #C9CBD5",

          }}
        >
          {headerLeft}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            {actions}
          </div>
        </div>

        {content}
      </div>

      <HealthSummaryHistoryModal
        field={healthHistoryField}
        onClose={() => setHealthHistoryField(null)}
        month={selectedMonth?.month}
        year={selectedMonth?.year}
      />

      <CopyForwardModal
        open={showCopyForwardModal}
        setOpen={setShowCopyForwardModal}
        monthOptions={copyForwardMonthOptions}
        currentData={copyForwardCurrentData}
        onCopy={handleCopyForward}
      />

      <CareplanReview
        open={showCareplanReviewModal}
        setOpen={setShowCareplanReviewModal}
        careplanId={careplanId}
        actionEnable={isHistoricalOrCurrentMonth(selectedMonth, currentMonth, currentYear)}
      />

      <Dialog open={showAssignNurseModal} setOpen={setShowAssignNurseModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Nurse</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="info-message">
              The patient’s care plan has been successfully assigned to the
              responsible nurse for review.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="ghost"
              size="default"
              onClick={() => setShowAssignNurseModal(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* "Include Condition" picker (per Figma) — opened when trying to
          include a 3rd condition instead of the plain reject toast, so the
          user can directly swap which two are billed. */}
      <Dialog open={showIncludeConditionModal} setOpen={setShowIncludeConditionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Only two code can be selected</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p style={{ color: "#565975", fontSize: "14px", margin: "0 0 16px" }}>
              You can bill a maximum of two ICD codes at a time.
            </p>
            <p style={{ color: "#0D1139", fontSize: "14px", fontWeight: 600, margin: "0 0 8px" }}>
              Billed Code
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {formData?.chronic_conditions?.map((condition) => {
                const key = condition.id ?? condition.tempId;
                return (
                  <label
                    key={key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      fontSize: "14px",
                      color: "#0D1139",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(includeDraft[key])}
                      onChange={() => handleIncludeDraftToggle(key)}
                    />
                    {condition.name} ({condition.icd_code})
                  </label>
                );
              })}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="ghost"
              size="default"
              onClick={() => setShowIncludeConditionModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="default"
              onClick={handleSaveIncludeConditions}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={statusAlertOpen}
        setOpen={setStatusAlertOpen}
        title="Notification"
        description={statusAlertMessage}
      >
        <Button variant="primary" onClick={() => setStatusAlertOpen(false)}>
          OK
        </Button>
      </AlertDialog>
    </>
  );
};

export default CarePlanNew;